import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ViewEncapsulation,
  ComponentFactoryResolver,
  Inject,
  Injector,
  ApplicationRef,
  ChangeDetectionStrategy,
  HostListener,
  NgZone,
  SecurityContext,
  QueryList,
  ViewChildren,
  Output,
  EventEmitter,
  signal,
  WritableSignal,
  Signal,
  computed,
  input,
  output
} from '@angular/core';
import { CHAT_ASSET_BASE_URL } from '../../tokens/chat-asset-base-url.token';
import { DomSanitizer } from '@angular/platform-browser';
import { EmbedVideoService } from '../../services/embed-video.service';
import { ChatConversationService } from '../../services/chat-conversation.service';
import { ChatAuthService } from '../../services/chat-auth.service';
import { MessageEx } from '../../classes/messageex';
import { ScrollingService } from '../../services/scrolling.service';
import { ContentTypes, ActionCardActions, ChatAnalyticAttributes } from '../../classes/constants';
import { ChatCloudLinkService } from '../../services/chat-cloudlink.service';
import { ChatAdminService } from '../../services/chat-admin.service';
import type {
  ChatConfig,
  ChatMessageEvent,
  QuoteMessageCite,
  QuoteMessage,
  MessageReaction,
  TextAreaProps
} from '../chat/chat.component';
import { MessageUtils } from '../../classes/messageutils';
import { SharedHelpers } from '../../classes/sharedhelpers';
import { TranslateService } from '@ngx-translate/core';
import escape from 'lodash-es/escape';
import uniqBy from 'lodash-es/uniqBy';
import { Subscription, Subject, defer } from 'rxjs';
import moment from 'moment';
import { ChatLoggingService } from '../../services/chat-logging.service';
import { ChatDatabaseService } from '../../services/chat-database.service';
import loadImage from 'blueimp-load-image';
import { filter, map, retryWhen, takeUntil } from 'rxjs/operators';
import { LocationService } from '../../services/location.service';
import { MentionHelpers } from '../../classes/mentionhelpers';
import { Menu } from 'primeng/menu';
import { PrimeThemeService } from '../../themes/prime-theme.service';
import { Popover } from 'primeng/popover';
import { Attachment, Participant } from '@mitel/cloudlink-sdk/chat';
import { User } from '@mitel/cloudlink-sdk/admin';
import { KeyboardKeys } from '../../classes/keyboard-keys';
import { EmojiPickerComponent } from '../emoji-picker/emoji-picker.component';
import {
  ActionCardBehaviorEvent,
  ActionCardMessageEvent,
  OpenLinkEvent,
  PostWebhookPayloadEvent,
  SendWebhookPayloadEvent,
  GetCurrentLocationEvent
} from './../../classes/actioncardhelpers';
import { QuotedMessageCardComponent } from '../cards/quoted-message-card/quoted-message-card.component';
import unescape from 'lodash-es/unescape';
import { AnalyticObject } from '../../interfaces/analytics-interfaces';
import { IChatMessageInfo } from '../../interfaces/chat-info-interfaces';
import { EmojiPipe } from '../../pipes/emoji.pipe';
import { default as AnalyticEvents } from '@mitel-internal/cloudlink-analytic-events/analytic-events.json';

@Component({
  selector: 'chat-item',
  templateUrl: 'chat-item.component.html',
  styleUrls: ['./chat-item.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EmojiPipe],
  standalone: false
})
export class ChatItemComponent implements OnInit {
  // ============================================================================
  // CONSTANTS (DO NOT convert to signals - static/readonly values)
  // ============================================================================
  private imageRegex = new RegExp('([^\\s]*(\\.jpeg|\\.jpg|\\.gif|\\.png|\\.jfif))($|\\s)', 'gi');
  private pdfRegex = new RegExp('([^\\s]*(\\.pdf))($|\\s)', 'gi');
  public reactionEmojis = ['👍', '👎', '😄', '😍', '😩', '🤨'];
  // Maps each reaction emoji to the same translation key used by the reaction-picker buttons above
  // (cl_chat.reactionOptions.*), so the reaction badge's aria-label - WCAG 3.2.4 - can state the
  // reaction's name rather than relying on the emoji character alone.
  private reactionNameKeys: { [emoji: string]: string } = {
    '👍': 'cl_chat.reactionOptions.thumbsUp',
    '👎': 'cl_chat.reactionOptions.thumbsDown',
    '😄': 'cl_chat.reactionOptions.happy',
    '😍': 'cl_chat.reactionOptions.love',
    '😩': 'cl_chat.reactionOptions.sad',
    '🤨': 'cl_chat.reactionOptions.question'
  };
  public contentTypes = ContentTypes;
  public actionCardActions = ActionCardActions;
  private maxQuotedInputCharacters = 764;
  private maxQuotedMessageCite = 47;

  // ============================================================================
  // LIFECYCLE & SUBSCRIPTIONS (DO NOT convert to signals - framework managed)
  // ============================================================================
  private participantsLoadedSub: Subscription = new Subscription();
  private participantsChangedSubs: Subscription = new Subscription();
  private userLoadedSub: Subscription = new Subscription();
  private observer: IntersectionObserver;
  private ngUnsubscribe: Subject<void> = new Subject();
  private mentionRefs = [];
  private inputFunction;

  // ============================================================================
  // INTERNAL FLAGS & CHECKS (used for one-time checks)
  // ============================================================================
  private attachmentMetaLoadCheck = false;
  private attachmentImageLoadCheck = false;
  public isCustomElement: boolean = false;
  public isMobile = false;
  public isOfflineCache: boolean;

  // ============================================================================
  // COMPUTED/DERIVED STATE (computed signals)
  // ============================================================================
  public isMine: WritableSignal<boolean> = signal(false);
  public isTextContentType: Signal<boolean> = computed(() => this.contentType() === this.contentTypes.TEXT);
  public isQuotedContentType: Signal<boolean> = computed(() => this.contentType() === this.contentTypes.QUOTED);
  public imageWidth: WritableSignal<number> = signal(undefined);
  public simpleType: WritableSignal<string> = signal('');
  private _actionCardMaxWidth: WritableSignal<string> = signal('22.5rem');
  public set actionCardMaxWidth(value: string) {
    this._actionCardMaxWidth.set(value);
  }
  public get actionCardMaxWidth(): string {
    if (this.messageContent && this.messageContent.nativeElement.offsetWidth > 360) {
      return this._actionCardMaxWidth();
    } else {
      return '100%';
    }
  }

  // ============================================================================
  // UI VISIBILITY STATE (drives template rendering)
  // ============================================================================
  public showOptionsArea: WritableSignal<boolean> = signal(false);
  public showMoreOptions: WritableSignal<boolean> = signal(false);
  public showReactionsArea: WritableSignal<boolean> = signal(false);
  public showMessageInfoOption: WritableSignal<boolean> = signal(false);
  public editMode: WritableSignal<boolean> = signal(false);
  public emojiDropdownVisible: WritableSignal<boolean> = signal(false);
  public showLocation: WritableSignal<boolean> = signal(true);
  public clickToLoadImage: WritableSignal<boolean> = signal(false);
  public imageLoading: WritableSignal<boolean> = signal(false);
  public overrideEmojiDropdownPos: WritableSignal<boolean> = signal(false);

  // ============================================================================
  // UI POSITION & LAYOUT (styling/positioning)
  // ============================================================================
  public moreOptionsRight = '1.0625rem';
  public moreOptionsTop = '1.1875rem';
  public currentTabIndex: WritableSignal<string> = signal('-1');

  // ============================================================================
  // MESSAGE CONTENT STATE (core message data)
  // ============================================================================
  public itemContent: WritableSignal<any> = signal('');
  public contentType: WritableSignal<string> = signal('');
  public bodyObject: WritableSignal<any> = signal(null);
  public fallbackText: WritableSignal<string> = signal('');
  public customAvatarSrc: WritableSignal<string> = signal('');

  // ============================================================================
  // ATTACHMENT STATE (file/media handling)
  // ============================================================================
  public attachmentName: WritableSignal<string> = signal('');
  public attachmentSize: WritableSignal<string> = signal('');
  public attachmentId: WritableSignal<string> = signal('');
  public isPdfAttachment: WritableSignal<any> = signal(null);
  public pdfViewerUrl: WritableSignal<any> = signal(null);
  private pdfUrl: WritableSignal<string> = signal('');
  public imageViewerUrl: WritableSignal<any> = signal(null);
  public blobUrl: WritableSignal<string> = signal('');
  public useImageViewer: WritableSignal<boolean> = signal(false);
  public mediaHeightRatio: WritableSignal<number> = signal(0);
  public isAttachmentImage: WritableSignal<boolean> = signal(false);
  public isNoPadding: WritableSignal<boolean> = signal(false);

  // ============================================================================
  // MESSAGE TYPE FLAGS (message classification)
  // ============================================================================
  public isVideoMessage: WritableSignal<boolean> = signal(false);
  public isVideoMessageWithContent: WritableSignal<boolean> = signal(false);
  public isImageMessageWithContent: WritableSignal<boolean> = signal(false);
  public isMediaMessage: WritableSignal<boolean> = signal(false);
  public isWhisper: WritableSignal<boolean> = signal(false);
  public isWhisperError: WritableSignal<boolean> = signal(false);
  public isSystemMessage: WritableSignal<boolean> = signal(false);
  public isErrorMessage: WritableSignal<boolean> = signal(false);
  public isAttachmentError: WritableSignal<boolean> = signal(false);
  public isHtmlMessage: WritableSignal<boolean> = signal(false);
  public isUpdatingMessage: WritableSignal<boolean> = signal(false);

  // ============================================================================
  // LOCATION STATE (geolocation data)
  // ============================================================================
  public latitude: WritableSignal<number> = signal(89.852595);
  public longitude: WritableSignal<number> = signal(12.726567);

  // ============================================================================
  // TRANSLATION STATE (i18n content)
  // ============================================================================
  public hasTranslation: WritableSignal<boolean> = signal(false);
  public isTranslated: WritableSignal<boolean> = signal(false);
  private translateOriginalText: WritableSignal<string> = signal('');
  private translatedText: WritableSignal<string> = signal('');

  // ============================================================================
  // REACTION STATE (user interactions)
  // ============================================================================
  public myReactions: WritableSignal<{ [reactionString: string]: string }> = signal({});
  public settingReaction: WritableSignal<{ [reactionString: string]: boolean }> = signal({});

  // ============================================================================
  // QUOTE/REPLY STATE (quoted message data)
  // ============================================================================
  public quoteMessage: WritableSignal<QuoteMessage> = signal(null);
  public quoteDate: WritableSignal<string> = signal('');
  public quoteTime: WritableSignal<string> = signal('');
  public quoteDateToday: WritableSignal<boolean> = signal(false);

  // ============================================================================
  // ACTION CARD STATE (template/card messages)
  // ============================================================================
  public actionCardImageNearBottom: WritableSignal<boolean> = signal(false);
  public actionCardScrolledToMessage: WritableSignal<boolean> = signal(false);

  // ============================================================================
  // EDIT MODE STATE (message editing flow)
  // ============================================================================
  public previewEditMessageContent: WritableSignal<string> = signal('');
  public isRemoveMessageSelected: WritableSignal<boolean> = signal(false);
  public _selectMessagesMode: boolean = false;
  public _isMessageSelected: boolean = false;
  private editQuotedObjString: WritableSignal<string> = signal('');
  public editQuotedBody: WritableSignal<string> = signal('');
  private lastMessageInput: WritableSignal<string> = signal('');
  public textAreaProps: WritableSignal<TextAreaProps> = signal({
    lastNode: null,
    lastCaretPos: null,
    message: null,
    maxInputCharacters: 1024,
    mentionInput: null,
    mentionCaretEnd: null,
    mentionNode: null,
    mentionPending: null,
    commandPending: null,
    mentions: [],
    commands: [],
    selectedDropdownIndex: 0,
    mentionFetchAttempt: 0,
    lastInputValue: null,
    lastInputString: null,
    mentionDropdownMargins: { bottom: 0, left: 0 }
  });

  // ============================================================================
  // VIEW CHILDREN & TEMPLATE REFERENCES
  // ============================================================================

  @ViewChild('video') video: ElementRef;
  @ViewChild('chatItem') chatItem: ElementRef;
  @ViewChild('textSpan') textSpan: ElementRef;
  @ViewChild('htmlMessage') htmlMessage: ElementRef;
  @ViewChild(QuotedMessageCardComponent) quotedMessageCardComponent: QuotedMessageCardComponent;
  @ViewChild('messageContent') messageContent: ElementRef;
  @ViewChild('emojiOptions') emojiOptions: ElementRef;
  @ViewChild('emojiButtonDiv') emojiButtonDiv: ElementRef;
  @ViewChild('chatMessage') chatMessage: ElementRef;
  @ViewChild('moreOptions') moreOptions: ElementRef;
  @ViewChild('messageOptions') messageOptions: ElementRef;
  @ViewChild('editTextArea') editTextArea: ElementRef;
  @ViewChild('emojisPopover') emojisPopover: Popover;
  @ViewChild('mentionsDropdown') mentionsDropdown: Menu;
  @ViewChild('mentionsAnchor') mentionsAnchor: ElementRef;
  @ViewChild(EmojiPickerComponent) emojiPicker: EmojiPickerComponent;
  @ViewChildren('dropdownMentions') dropdownMentions: QueryList<ElementRef>;
  @ViewChild('quotecontent') quoteContent: ElementRef;

  // --------------------------------------------------------------------------
  // Core Required Inputs
  // --------------------------------------------------------------------------
  item = input.required<MessageEx>();
  config = input<ChatConfig>();
  reactions = input<{
    [messageId: string]: { [reactionString: string]: MessageReaction[] };
  }>({});
  participants = input<Participant[]>([]);

  // --------------------------------------------------------------------------
  // Message Context & State
  // --------------------------------------------------------------------------
  chatItems = input<QueryList<ChatItemComponent>>();
  loading = input<boolean>();
  textAreaDisabled = input<boolean>();
  isOffline = input<boolean>();
  // True for every message in an AI-mode conversation (both the assistant's replies and the
  // user's own turns sent to it), not just messages the assistant authored - reactions, replies/
  // quoting, translation and PDF preview are all human-to-human chat features with no MAIS-backed
  // equivalent, so the whole message-options toolbar is dead UI here regardless of which side
  // sent a given message. See showMessageOptions().
  aiMode = input<boolean>(false);
  // Set by ChatComponent only for the one message that should render chips (its
  // clCompChat_quickReplies and/or the aiConfig.handoff chip) - the last message in the
  // conversation, once it has finished streaming.
  showAiQuickReplies = input<boolean>(false);
  aiQuickRepliesDisabled = input<boolean>(false);
  aiHandoffLabel = input<string | undefined>(undefined);

  // --------------------------------------------------------------------------
  // View Mode Flags (TBD: except previewMode)
  // --------------------------------------------------------------------------
  isSnapshot = input<boolean>();
  isSearch = input<boolean>();
  isContinuation = input<boolean>(false);
  goingToQuoted = input<boolean>(false);
  @Input() previewMode: boolean;

  // --------------------------------------------------------------------------
  // Edit & Info Mode State (TBD: except editModeOpen)
  // --------------------------------------------------------------------------
  @Input() editModeOpen: boolean; // Note: Cannot convert - modified internally (two-way binding)
  currentEditMessageId = input<string>();
  currentMessageInfoId = input<string>();
  isMessageInfoOpen = input<boolean>();

  // --------------------------------------------------------------------------
  // Selection Mode (TBD: has getter/setter logic)
  // --------------------------------------------------------------------------
  @Input() set selectMessagesMode(value: boolean) {
    this._selectMessagesMode = value;
    if (value === false) {
      this.isMessageSelected = false;
      this.previewMode = false;
    } else {
      this.previewMode = true;
      if (this.editMode()) {
        this.closeEditMode();
      }
    }
    this.cdRef.markForCheck();
  }

  get selectMessagesMode(): boolean {
    return this._selectMessagesMode;
  }

  @Input() set isMessageSelected(isMessageSelected: boolean) {
    this._isMessageSelected = isMessageSelected;
    this.cdRef.markForCheck();
  }

  get isMessageSelected(): boolean {
    return this._isMessageSelected;
  }

  // --------------------------------------------------------------------------
  // Feature Flags
  // --------------------------------------------------------------------------
  noReadIndicators = input<boolean>();
  smsMessage = input<boolean>();
  smsMode = input<boolean>();
  isRecording = input<boolean>();

  // --------------------------------------------------------------------------
  // Configuration Settings
  // --------------------------------------------------------------------------
  maxImageSize = input<number>();
  dropdownMaxHeight = input<string>();
  emojiDictionary = input<any>();
  dateTimeLocale = input<string>();

  // --------------------------------------------------------------------------
  // Function Overrides (remain as functions)
  // --------------------------------------------------------------------------
  @Input() downloadAttachmentOverride: Function;
  @Input() fullScreenPdfOverride: Function;

  // --------------------------------------------------------------------------
  // Selection Mode Events
  // --------------------------------------------------------------------------
  onSelectMessagesMode = output<boolean>();
  onMessageSelected = output<boolean>();

  // --------------------------------------------------------------------------
  // Edit Mode Events
  // --------------------------------------------------------------------------
  editModeOpenChange = output<boolean>();
  currentEditMessageIdChange = output<string>();

  // --------------------------------------------------------------------------
  // Message Interaction Events
  // --------------------------------------------------------------------------
  onQuotedMessageChange = output<QuoteMessageCite>();
  onMessageContextMenu = output<ChatMessageEvent>();
  onRetrySend = output<any>();
  onDeletePendingSend = output<string>();

  // --------------------------------------------------------------------------
  // Navigation Events
  // --------------------------------------------------------------------------
  onGoToMessage = output<any>();
  onGoToAiSuggestionsMessage = output<string>();
  onAiQuickReply = output<string>();
  onAiHandoff = output<void>();
  onWindowOpen = output<string>();
  onOpenModal = output<any>();

  // --------------------------------------------------------------------------
  // Media/Attachment Events
  // --------------------------------------------------------------------------
  onFullScreenPdf = output<any>();
  onFullScreenImage = output<any>();

  // --------------------------------------------------------------------------
  // User Interaction Events
  // --------------------------------------------------------------------------
  onNameClicked = output<string>();
  onAvatarMouseEnter = output<any>();
  onAvatarMouseLeave = output<any>();
  onAvatarContextMenu = output<any>();

  // --------------------------------------------------------------------------
  // Action Card Events
  // --------------------------------------------------------------------------
  onChatItemActionCardEvent = output<string>();
  onChatItemActionCardMessage = output<ActionCardMessageEvent>();
  onChatItemActionCardBehavior = output<ActionCardBehaviorEvent>();
  onChatItemOpenLink = output<OpenLinkEvent>();
  onChatItemPostWebhookPayload = output<PostWebhookPayloadEvent>();
  onChatItemSendWebhookPayload = output<SendWebhookPayloadEvent>();

  // --------------------------------------------------------------------------
  // System Events
  // --------------------------------------------------------------------------
  onShowWarning = output<string>();
  onGetCurrentLocation = output<GetCurrentLocationEvent>();

  @HostListener('document:click', ['$event']) onClick(event: MouseEvent | TouchEvent) {
    if (
      (this.showReactionsArea() || this.showMoreOptions()) &&
      (!this.emojiOptions || !this.emojiOptions.nativeElement.contains(event.target)) &&
      (!this.moreOptions || !this.moreOptions.nativeElement.contains(event.target)) &&
      (!this.messageOptions || !this.messageOptions.nativeElement.contains(event.target))
    ) {
      this.closeOptions();
    }

    if (this.mentionsDropdown) {
      this.mentionsDropdown.hide();
      this.cdRef.markForCheck();
    }
  }

  constructor(
    private sanitizer: DomSanitizer,
    private embedVideoService: EmbedVideoService,
    private chatConversationService: ChatConversationService,
    public chatAuthService: ChatAuthService,
    private scrollingService: ScrollingService,
    public chatCloudLinkService: ChatCloudLinkService,
    public chatAdminService: ChatAdminService,
    private cdRef: ChangeDetectorRef,
    public translateService: TranslateService,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    private chatLoggingService: ChatLoggingService,
    private chatDatabaseService: ChatDatabaseService,
    public locationService: LocationService,
    private ngZone: NgZone,
    private emojiPipe: EmojiPipe,
    public primeThemeService: PrimeThemeService,
    @Inject(CHAT_ASSET_BASE_URL) private assetBaseUrl: string
  ) {
    this.isMobile = SharedHelpers.isMobile();
    this.emojiPipe = new EmojiPipe(sanitizer);
  }

  get currentTheme() {
    return this.primeThemeService.currentTheme();
  }

  ngOnInit() {
    if (this.config()?.useCustomSystemAvatar) {
      this.customAvatarSrc.set(this.config().customAvatarFile);
    } else {
      this.customAvatarSrc.set('');
    }

    if (window.customElements) {
      this.isCustomElement = !!customElements.get('cloudlink-chat');
    }
    this.parseMessage();

    this.chatCloudLinkService.attachmentsChanged$
      .pipe(
        filter(attachment => (<any>attachment).conversationId === this.item().conversationId),
        filter(
          attachment =>
            attachment.name === this.attachmentName() || attachment.name === this.item().clCompChat_dbAttachmentName
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((attachment: Attachment) => {
        if (!!attachment && attachment.displayName) {
          // CL-13820
          this.attachmentName.set(attachment.displayName);
          this.item().clCompChat_dbAttachmentName = this.attachmentName();
          this.chatLoggingService.info({
            logMessage: 'chat-item.component.updateAttachment',
            logParameters: [attachment.displayName]
          });
          this.cdRef.markForCheck();
        }
      });

    //subscribe to participant changed
    this.participantsChangedSubs.add(
      this.chatCloudLinkService.participantsChanged$
        .pipe(
          map(event => <Participant>event),
          filter(p => p.conversationId === this.item().conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            if (this.isMessageInfoOpen() && this.currentMessageInfoId() === this.item().messageId) {
              this.onOpenModal.emit({
                modal: 'MessageInfoComponent',
                chatItem: this
              });
            }
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat-item.component.initialize(): Error in subscribe participantAdded',
              logParameters: [ex]
            });
          }
        })
    );
    //subscribe to participant deleted
    this.participantsChangedSubs.add(
      this.chatCloudLinkService.participantsDeleted$
        .pipe(
          map(event => <Participant>event),
          filter(p => p.conversationId === this.item().conversationId),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resource => {
          try {
            if (this.isMessageInfoOpen() && this.currentMessageInfoId() === this.item().messageId) {
              this.onOpenModal.emit({
                modal: 'MessageInfoComponent',
                chatItem: this
              });
            }
          } catch (ex) {
            // stifle exception to prevent unsubscribing
            this.chatLoggingService.error({
              logMessage: 'chat-item.component.initialize(): Error in subscribe participantAdded',
              logParameters: [ex]
            });
          }
        })
    );
  }

  ngAfterViewInit() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const tabindex = entry.isIntersecting ? '0' : '-1';
          this.currentTabIndex.set(tabindex);

          //Need to deal with dynamic elements
          if (this.chatItem?.nativeElement) {
            this.checkFocusItems(this.chatItem.nativeElement);
            this.checkIFrameTabIndex();
          }

          this.cdRef.markForCheck();
        },
        {
          root: document.getElementById('chat-main'),
          // threshold 1.0 doesn't work on some chrome versions
          threshold: 0.9
        }
      );

      if (this.chatItem?.nativeElement) {
        this.observer.observe(this.chatItem.nativeElement);
      }
    }
  }

  checkIFrameTabIndex() {
    let iframes = this.chatItem?.nativeElement?.querySelectorAll('iframe');
    if (iframes?.length) {
      iframes.forEach(iframe => {
        iframe.tabIndex = this.currentTabIndex();
      });
    }
  }

  checkFocusItems(element) {
    let checkArray = ['a', 'button'];
    checkArray.forEach(a => {
      let focusItems = element?.querySelectorAll(a);
      if (focusItems?.length) {
        focusItems.forEach(h => {
          h.tabIndex = this.currentTabIndex();
        });
      }
    });
  }

  ngOnDestroy() {
    this.participantsLoadedSub.unsubscribe();
    this.participantsChangedSubs.unsubscribe();
    this.userLoadedSub.unsubscribe();
    if (this.blobUrl()) {
      window.URL.revokeObjectURL(this.blobUrl());
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.editTextArea) {
      this.editTextArea.nativeElement.removeEventListener('input', this.inputFunction);
    }
    if (this.mentionRefs.length) {
      this.mentionRefs.forEach(r => {
        this.appRef.detachView(r.hostView);
        r.destroy();
      });
    }
    if ('IntersectionObserver' in window && this.observer) {
      if (this.chatItem?.nativeElement) {
        this.observer.unobserve(this.chatItem.nativeElement);
      }
      this.observer.disconnect();
    }
    window.removeEventListener('keydown', this.subscribeKeyDown);
  }

  private disableNodesTabIndex(node) {
    node.tabIndex = -1;
    let nodes = node.childNodes;
    if (nodes?.length) {
      nodes.forEach(n => {
        this.disableNodesTabIndex(n);
      });
    }
  }

  async parseMessage() {
    var rendered = false;
    let itemBody = this.item().body;
    this.isOfflineCache = false;
    //We ignore subsequent attempts to load an attachment at the moment
    if (!(this.isErrorMessage() && this.item()?.contentType === ContentTypes.ATTACHMENT)) {
      this.contentType.set(this.item().contentType);
    }
    this.isMine.set(this.item().createdBy === this.chatAuthService.userId);
    // isTextContentType and isQuotedContentType are now computed from contentType signal
    this.fallbackText.set(this.item().fallbackText || '');
    if (itemBody) {
      try {
        if (this.isOffline() && this.indexDbEnabled()) {
          this.isOfflineCache = true;
        }

        this.item().clCompChat_displayBody = this.item().body;
        //Need to escape any HTML for a TEXT message
        if (this.contentType() === ContentTypes.TEXT) {
          if (!this.config() || !this.config().hideTranslatedBody) {
            if (
              this.item()._embedded &&
              this.item()._embedded.translations &&
              this.item()._embedded.translations._embedded &&
              this.item()._embedded.translations._embedded.items &&
              this.item()._embedded.translations._embedded.items[0].body !== this.item().body
            ) {
              //Message was changed
              if (this.translatedText() !== this.item()._embedded.translations._embedded.items[0].body) {
                this.hasTranslation.set(false);
              }
              if (!this.hasTranslation()) {
                this.translateOriginalText.set(this.item().body);
                this.translatedText.set(this.item()._embedded.translations._embedded.items[0].body);
                this.hasTranslation.set(true);
                this.isTranslated.set(true);
              }
              if (this.isTranslated()) {
                this.item().clCompChat_displayBody = this.translatedText();
              } else {
                this.item().clCompChat_displayBody = this.translateOriginalText();
              }
            } else {
              this.hasTranslation.set(false);
            }
          }
          this.item().clCompChat_displayBody = escape(this.item().clCompChat_displayBody);
        }
        if (
          this.isInternetVideo(itemBody) &&
          (this.contentType() === ContentTypes.HTML || this.contentType() === ContentTypes.TEXT) &&
          !this.isOfflineCache
        ) {
          this.mediaHeightRatio.set(1.783060921248143);
          let mediaString: any = this.embedVideoService.embed(itemBody);
          if (mediaString) {
            this.itemContent.set(mediaString);
            this.contentType.set(ContentTypes.HTML);
            this.isVideoMessage.set(true);
            if (this.itemContent()) {
              this.isMediaMessage.set(true);
            }
            this.isVideoMessageWithContent.set(this.isInternetVideoWithContent(itemBody));
            if (this.isVideoMessageWithContent()) {
              //Handle mentions
              this.checkMentions(this.item(), 'clCompChat_displayBody', () => {
                return this.htmlMessage;
              });
              this.checkEmojis(this.item(), 'clCompChat_displayBody');
            }
          }
        } else if (MessageUtils.isAttachment(this.item()) || MessageUtils.isAttachmentSystemMessage(this.item())) {
          var nearBottom = (this.loading() && !this.isSnapshot()) || this.scrollingService.isNearBottom();
          var scrolledToMessage = this.isSnapshot() && !!this.scrollingService.lastScrolledTo.messageId;
          try {
            this.simpleType.set(this.getSimpleType(this.item().clCompChat_attachmentContentType || ''));
            if (
              (this.simpleType() == 'audio' || this.simpleType() == 'video') &&
              (!this.isOffline() || !this.indexDbEnabled())
            ) {
              this.isNoPadding.set(true);
            }

            let attachmentBody = '';
            if (MessageUtils.isAttachment(this.item())) {
              attachmentBody = this.item().body;
            } else {
              if (this.item().clCompChat_hidden === undefined && !this.item().clCompChat_dbAttachmentName) {
                this.item().clCompChat_hidden = true;
              }
              attachmentBody = MessageUtils.getAttachmentSystemMessageBody(this.item());
              this.item().clCompChat_displayBody = attachmentBody;
            }
            this.attachmentId.set(attachmentBody);

            this.attachmentName.set(this.item().clCompChat_dbAttachmentName || '');
            this.attachmentSize.set(this.item().clCompChat_dbAttachmentSize || '');

            if (!this.isOfflineCache && this.simpleType() === 'image') {
              this.isAttachmentImage.set(true);
            }

            //Render what we can before the await
            this.cdRef.markForCheck();

            if (
              (this.isOfflineCache || this.config()?.jsonMessagesAsSource) &&
              !this.attachmentSize() &&
              this.item().tag
            ) {
              try {
                let tag = JSON.parse(this.item().tag);
                if (tag.attachmentSize) {
                  this.attachmentSize.set(tag.attachmentSize);
                }
              } catch (ex) {
                this.chatLoggingService.error({
                  logMessage: 'chat-item.component.parseMessage(): Error parsing offline attachment tag',
                  logParameters: [this.item().messageId, ex]
                });
              }
            }

            if (!this.isOfflineCache) {
              //Need to always check Attachment System Message for meta in case of a delete
              if (
                !this.attachmentMetaLoadCheck &&
                (!this.attachmentName() ||
                  !this.attachmentSize() ||
                  MessageUtils.isAttachmentSystemMessage(this.item()))
              ) {
                this.attachmentMetaLoadCheck = true;
                let attachmentData;
                if (this.config()?.jsonMessagesAsSource) {
                  attachmentData = {
                    displayName: this.item().fallbackText
                  };
                } else {
                  const resAttachmentData = await this.chatCloudLinkService.chat.getAttachmentMeta({
                    conversationId: this.item().conversationId,
                    attachmentId: attachmentBody
                  });
                  attachmentData = resAttachmentData.body;
                  this.attachmentSize.set(SharedHelpers.getFileSize(attachmentData.length));
                }

                // need to strip the html anchor from the display name if not using jsonMessagesAsSource
                const attachmentDisplayName = MessageUtils.getAttachmentDisplayName(
                  attachmentData,
                  !this.config()?.jsonMessagesAsSource
                );
                this.chatLoggingService.info({
                  logMessage: 'chat-item.component.parseMessage(): Attachment data retrieved',
                  logParameters: [this.item().conversationId, attachmentBody, { attachmentName: attachmentDisplayName }]
                });

                this.item().clCompChat_hidden = false;
                this.attachmentName.set(attachmentDisplayName);
                //For uploaded attachments using platform system messages
                if (
                  this.attachmentName() === this.attachmentId() &&
                  this.chatConversationService.pendingAttachments[this.attachmentId()]?.attachmentName
                ) {
                  this.attachmentName.set(
                    this.chatConversationService.pendingAttachments[this.attachmentId()].attachmentName
                  );
                }

                this.item().clCompChat_dbAttachmentName = this.attachmentName();
                this.item().clCompChat_dbAttachmentSize = this.attachmentSize();
                try {
                  await this.chatDatabaseService.updateAttachmentMessage(
                    this.item().messageId,
                    this.chatAuthService.accountId || this.item().accountId,
                    this.attachmentName(),
                    this.attachmentSize()
                  );
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat-item.component.parseMessage(): Error updating attachment message in db',
                    logParameters: [this.item().messageId, ex]
                  });
                }
              }
              if (!this.item().clCompChat_hidden) {
                if (this.simpleType() === 'image') {
                  let imageSize = SharedHelpers.getBytesSize(this.attachmentSize());
                  if (Number(imageSize) > this.maxImageSize()) {
                    this.isAttachmentImage.set(false);
                    this.clickToLoadImage.set(true);
                  } else {
                    await this.loadImageAttachment(attachmentBody);
                  }
                } else if (this.simpleType() == 'pdf' && !SharedHelpers.isMobile()) {
                  this.isPdfAttachment.set(true);
                }
              }
              if (!this.cdRef['destroyed']) {
                this.cdRef.markForCheck();
                rendered = true;
              }
            } else {
              this.item().clCompChat_hidden = false;
            }

            //Check one last time, race condition
            if (!scrolledToMessage) {
              scrolledToMessage = this.isSnapshot() && !!this.scrollingService.lastScrolledTo.messageId;
            }

            setTimeout(() => {
              if (nearBottom && !this.goingToQuoted() && !scrolledToMessage) {
                this.scrollingService.scrollToBottom();
              } else if (scrolledToMessage && !this.goingToQuoted()) {
                this.scrollingService.scrollToMessage(this.scrollingService.lastScrolledTo.messageId);
              }
            }, 0);
          } catch (ex) {
            if (ex?.statusCode === 404 && MessageUtils.isAttachmentSystemMessage(this.item())) {
              this.chatLoggingService.info({
                logMessage: 'chat-item.component.parseMessage(): Hiding deleted attachment system message',
                logParameters: [this.item().conversationId, this.item().messageId]
              });
              this.chatCloudLinkService.sendMessagesDeleted(this.item().messageId, true);
              this.isAttachmentError.set(false);
            } else {
              this.isErrorMessage.set(true);
              if (ex?.statusCode !== 404) {
                this.isAttachmentError.set(true);
                this.chatLoggingService.error({
                  logMessage: 'chat-item.component.parseMessage(): Error loading attachment message',
                  logParameters: [this.item().conversationId, this.item().messageId]
                });
              } else {
                this.isAttachmentError.set(false);
              }
              this.contentType.set(ContentTypes.ERROR);
            }
          }
        } else if (
          itemBody.match(this.imageRegex) &&
          (this.contentType() === ContentTypes.HTML || this.contentType() === ContentTypes.TEXT)
        ) {
          //Need to reset for recovery from offline mode
          this.isImageMessageWithContent.set(false);
          let url = itemBody;
          var matches = itemBody.match(this.imageRegex);
          if (matches) {
            url = matches[0].trim();
            if (url !== itemBody) {
              this.isImageMessageWithContent.set(true);
            }
            this.contentType.set(ContentTypes.HTML);
            this.imageLoading.set(true);
            this.cdRef.markForCheck();
            this.loadImage(url);
          }
          if (this.isImageMessageWithContent()) {
            //Handle mentions
            this.checkMentions(this.item(), 'clCompChat_displayBody', () => {
              return this.htmlMessage;
            });
            this.checkEmojis(this.item(), 'clCompChat_displayBody');
          }
        } else if (
          itemBody.match(this.pdfRegex) &&
          (this.contentType() === ContentTypes.HTML || this.contentType() === ContentTypes.TEXT) &&
          !this.isOfflineCache
        ) {
          this.mediaHeightRatio.set(0.77272727272);
          let url = itemBody;
          var matches = itemBody.match(this.pdfRegex);
          var isPdfMessageWithContent = false;
          if (matches) {
            var foundMatch = false;
            matches.forEach(m => {
              if (!foundMatch) {
                url = m.trim();
                if (url !== itemBody) {
                  isPdfMessageWithContent = true;
                }
                if (!SharedHelpers.isMobile() && url.toLowerCase().indexOf('https') === 0) {
                  this.pdfViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
                  this.pdfUrl.set(url);
                  foundMatch = true;
                }
              }
            });
          }
          if (isPdfMessageWithContent) {
            if (this.contentType() === ContentTypes.HTML) {
              this.checkMentions(this.item(), 'clCompChat_displayBody', () => {
                return this.htmlMessage;
              });
              this.checkEmojis(this.item(), 'clCompChat_displayBody');
            } else if (this.contentType() === ContentTypes.TEXT) {
              this.checkMentions(this.item(), 'clCompChat_displayBody', () => {
                return this.textSpan;
              });
              this.checkEmojis(this.item(), 'clCompChat_displayBody');
            }
          }
        } else if (MessageUtils.isLocation(this.item())) {
          if (this.item().clCompChat_hideLocation) {
            this.showLocation.set(false);
          }
          this.parseLocation();
          //TODO Figure out how to enable the right google maps elements when visible for tabbing
          setTimeout(() => {
            if (this.chatItem?.nativeElement) {
              let mapNode = this.chatItem.nativeElement.getElementsByTagName('google-map');
              if (mapNode?.length) {
                this.disableNodesTabIndex(mapNode[0]);
              }
            }
          }, 2000);
        } else if (
          MessageUtils.isActionCard(this.item()) ||
          MessageUtils.isVoicemail(this.item()) ||
          MessageUtils.isMissedCall(this.item())
        ) {
          if (MessageUtils.isVoicemail(this.item()) || MessageUtils.isMissedCall(this.item())) {
            this.isNoPadding.set(true);
          }

          // The body in templates are provided as string data... attempt a parse and handle failure if garbage is thrown at us
          try {
            this.bodyObject.set(JSON.parse(itemBody));
            // Image URLs are used as card headers. They maybe internet URLs OR a reference to a file attachment using file://{attachmentId}
            if (this.bodyObject().headerImageUrl) {
              const imagePath: string = this.bodyObject().headerImageUrl;
              if (imagePath.indexOf('file://') >= 0) {
                // this is a file reference.. which is a reference to an attachment
                const match = imagePath.match(this.imageRegex);
                if (match) {
                  // parse out the file name
                  const slashIndex = imagePath.lastIndexOf('/');
                  const attachmentId = imagePath.substring(slashIndex + 1);
                  this.isImageMessageWithContent.set(true);
                  this.loadImageAttachment(attachmentId);
                }
              } else {
                this.imageViewerUrl.set(imagePath);
              }
              this.actionCardImageNearBottom.set(
                (this.loading() && !this.isSnapshot()) || this.scrollingService.isNearBottom()
              );
              this.actionCardScrolledToMessage.set(
                this.isSnapshot() && !!this.scrollingService.lastScrolledTo.messageId
              );
            }
            if (this.bodyObject().bodyContentType === ContentTypes.LOCATION && this.bodyObject().body) {
              this.parseLocation(this.bodyObject().body);
            }
          } catch (ex) {
            this.chatLoggingService.error({
              logMessage: 'chat-item.component.parseMessage(): Error parsing template',
              logParameters: [ex]
            });
            this.contentType.set('UNKNOWN');
          }
        } else if (MessageUtils.isQuoteMessage(this.item())) {
          this.quoteMessage.set(JSON.parse(itemBody));
          this.quoteMessage.update(msg => {
            msg.cite.body = escape(msg.cite.body);
            return msg;
          });
          if (this.quoteMessage().cite.createdOn) {
            var createdOn = moment(this.quoteMessage().cite.createdOn);
            this.quoteDate.set(createdOn.format('MM/DD'));
            this.quoteTime.set(createdOn.format('LT'));
            this.quoteDateToday.set(moment(createdOn).isSame(moment(), 'day'));
          }
          //Handle mentions
          if (this.quoteMessage().content.contentType === ContentTypes.TEXT) {
            this.quoteMessage.update(msg => {
              msg.content.body = escape(msg.content.body);
              return msg;
            });
          }
          setTimeout(() => {
            if (this.quotedMessageCardComponent) {
              this.quotedMessageCardComponent.markForCheck();
              this.checkMentions(this.quoteMessage().cite, 'body', () => {
                return this.quotedMessageCardComponent.quoteCitedBody;
              });
              this.checkEmojis(this.quoteMessage().cite, 'body');
              this.checkMentions(this.quoteMessage().content, 'body', () => {
                return this.quoteContent;
              });
              this.checkEmojis(this.quoteMessage().content, 'body');
            }
          });
        } else if (MessageUtils.isWhisperMessage(this.item())) {
          this.isWhisper.set(true);
          if (MessageUtils.isWhisperError(this.item())) {
            this.isWhisperError.set(true);
            this.item().clCompChat_displayBody = this.item().clCompChat_displayBody.replace(
              MessageUtils.errorString,
              ''
            );
          }
        } else if (MessageUtils.isSystemMessage(this.item())) {
          this.chatLoggingService.debug({
            logMessage: 'chat-item.component.parseMessage() isSystemMessage',
            logParameters: [this.item().conversationId]
          });
          this.isSystemMessage.set(true);
          var systemMessage = JSON.parse(itemBody);
          if (systemMessage.fallbackText) {
            this.item().clCompChat_displayBody = systemMessage.fallbackText;
          } else if (this.item().fallbackText) {
            this.item().clCompChat_displayBody = this.item().fallbackText;
          }
          if (systemMessage.lookupCode) {
            var data = {};
            if (systemMessage.data) {
              this.chatLoggingService.debug({
                logMessage: 'chat-item.component.parse() isSystemMessage has data',
                logParameters: [this.item().conversationId]
              });
              for (var i = 0; i < systemMessage.data.length; i++) {
                this.chatLoggingService.debug({
                  logMessage: 'chat-item.component.parse() isSystemMessage system message data',
                  logParameters: [this.item().conversationId, i, systemMessage.data[i]]
                });
                if (typeof systemMessage.data[i] === 'string' && systemMessage.data[i].indexOf('@') === 0) {
                  data[i] = this.getDisplayName(systemMessage.data[i].substring(1));
                } else {
                  data[i] = systemMessage.data[i];
                }
                this.chatLoggingService.debug({
                  logMessage: 'chat-item.component.parse() isSystemMessage data',
                  logParameters: [this.item().conversationId, i, data[i]]
                });
              }
            }
            if (systemMessage.lookupCode === 'conversation-updated-visibility') {
              if (data[1] === 'PRIVATE') {
                data[1] = this.translateService.instant('cl_chat.private').toLowerCase();
              } else if (data[1] === 'PUBLIC') {
                data[1] = this.translateService.instant('cl_chat.public').toLowerCase();
              }
            }
            let lookupTranslation = this.translateService.instant('cl_chat.' + systemMessage.lookupCode, data);
            if (lookupTranslation === 'cl_chat.' + systemMessage.lookupCode) {
              if (systemMessage.lookupText) {
                var lookupText = systemMessage.lookupText;
                if (systemMessage.data) {
                  for (var i = 0; i < systemMessage.data.length; i++) {
                    lookupText = lookupText.replace('${' + i + '}', data[i]);
                    lookupText = lookupText.replace('{' + i + '}', data[i]);
                  }
                }
                this.item().clCompChat_displayBody = lookupText;
              }
            } else {
              this.item().clCompChat_displayBody = lookupTranslation;
            }
            if (!this.cdRef['destroyed']) {
              this.cdRef.markForCheck();
              rendered = true;
            }
          }
        } else if (this.contentType() === ContentTypes.HTML) {
          this.isHtmlMessage.set(true);
          //Handle mentions
          this.checkMentions(this.item(), 'clCompChat_displayBody', () => {
            return this.htmlMessage;
          });
          this.checkEmojis(this.item(), 'clCompChat_displayBody');
        } else if (this.contentType() === ContentTypes.TEXT) {
          //Handle mentions for text
          this.checkMentions(this.item(), 'clCompChat_displayBody', () => {
            return this.textSpan;
          });
          this.checkEmojis(this.item(), 'clCompChat_displayBody');
        }
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat-item.component.parseMessage(): Error loading message',
          logParameters: [this.item().messageId, this.item().contentType, ex]
        });
        this.isErrorMessage.set(true);
        this.contentType.set(ContentTypes.ERROR);
      }
    } else {
      this.item().clCompChat_displayBody = '';
    }
    if (this.item()?._embedded?.reactions?.count) {
      this.updateReactions(this.item()._embedded.reactions);
    }
    if (!this.cdRef['destroyed'] && !rendered) {
      this.cdRef.markForCheck();
      setTimeout(() => {
        this.checkFocusItems(this.chatItem?.nativeElement);
        this.checkIFrameTabIndex();
      });
    }
  }

  private checkEmojis(item, bodyRef) {
    if (this.isMobile || !this.config()?.enableTwemoji) {
      return;
    }
    let emojis = unescape(item[bodyRef]).match(SharedHelpers.emojiRejex);
    var error = false;
    var oldBody = item[bodyRef];
    if (emojis && emojis.length) {
      setTimeout(() => {
        emojis
          .filter((val, i, arr) => arr.indexOf(val) == i)
          .forEach(emoji => {
            this.ngZone.run(() => {
              try {
                if (!error) {
                  let codePoint = SharedHelpers.getEmojiUnicode(emoji);
                  let emojiSvg = this.emojiPipe.transform(codePoint)['changingThisBreaksApplicationSecurity'] as string;
                  //replace native emoji with emojiSvg in the message
                  item[bodyRef] = SharedHelpers.replaceAll(item[bodyRef], emoji, emojiSvg, false);
                }
              } catch (ex) {
                this.chatLoggingService.error({
                  logMessage: 'chat-item.component.parseMessage(): Error handling emojis',
                  logParameters: [ex]
                });
                error = true;
              }
            });
          });
        if (error) {
          item[bodyRef] = oldBody;
        }
        this.cdRef.markForCheck();
      }, 0);
    }
  }

  private checkMentions(item, bodyRef, getElement: Function) {
    //convert HTML special entities ' " < to their corresponding characters
    let mentions: string[] | null = unescape(item[bodyRef]).match(SharedHelpers.mentionsRegexGlobal);
    if (mentions && mentions.length) {
      mentions = MentionHelpers.getFilteredMentions(mentions);
      var replaceMentions = uniqBy(mentions, v => v);
      var error = false;
      var oldBody = item[bodyRef];
      replaceMentions.forEach(m => {
        item[bodyRef] = MentionHelpers.replaceWithMentionPlaceholder(item[bodyRef], m);
      });
      setTimeout(() => {
        mentions.forEach(u => {
          this.ngZone.run(() => {
            try {
              if (!error) {
                let mentionReturn = MentionHelpers.replaceWithMentionElement(
                  u,
                  getElement,
                  this.resolver,
                  this.injector,
                  this.appRef
                );
                if (mentionReturn.ref) {
                  this.mentionRefs.push(mentionReturn.ref);
                } else if (mentionReturn.noMatch) {
                  item[bodyRef] = MentionHelpers.removeMentionPlaceholder(item[bodyRef], u);
                }
              }
            } catch (ex) {
              this.chatLoggingService.error({
                logMessage: 'chat-item.component.parseMessage(): Error handling mentions',
                logParameters: [ex]
              });
              error = true;
            }
          });
        });
        if (error) {
          item[bodyRef] = oldBody;
        }
        // NOTE: detectChanges() is required here instead of markForCheck()
        this.cdRef.detectChanges();
      }, 0);
      // NOTE: detectChanges() is required here instead of markForCheck()
      this.cdRef.detectChanges();
      if (this.isQuotedContentType()) {
        this.editQuotedBody.set(item[bodyRef]);
      }
    }
  }

  isInternetVideo(url: string): boolean {
    return !!url.match(this.embedVideoService.videoRegex);
  }

  isInternetVideoWithContent(body: string): boolean {
    if (!this.isInternetVideo(body)) {
      return false;
    }
    var match = body.match(this.embedVideoService.videoRegex);
    if (match) {
      return match[0] !== body;
    }
    return false;
  }

  getSimpleType(attachmentContentType: string): string {
    if (!attachmentContentType) return '';
    let ret = attachmentContentType.substring(0, attachmentContentType.indexOf('/'));
    if (ret === 'image' && this.config() && this.config().disableImageAttachmentRender) {
      return '';
    }
    return ret;
  }

  async downloadAttachment(attachmentId: string) {
    this.chatLoggingService.info({
      logMessage: 'chat-item.component.downloadAttachment()',
      logParameters: [attachmentId]
    });
    this.closeOptions();
    try {
      if (this.downloadAttachmentOverride) {
        this.downloadAttachmentOverride({
          conversationId: this.item().conversationId,
          attachmentId: attachmentId,
          name: this.attachmentName()
        });
      } else {
        await this.chatConversationService.downloadAttachment({
          conversationId: this.item().conversationId,
          attachmentId: attachmentId,
          name: this.attachmentName()
        });
      }
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat-item.component.downloadAttachment(): Error',
          logParameters: [ex]
        },
        errorMessage: this.translateService.instant('cl_chat.downloadFailedTryAgain')
      });
    }
  }

  public launchActionCardDialer($event) {
    this.launchDialer.apply(this, Object.values($event));
  }

  public launchDialer($event: Event, tel: string, title: string, behavior: any) {
    if ($event) {
      $event.preventDefault;
    }
    // Append standard tel: link to 'tel:' if not provided and launch standard dialer
    let telUrl: string = tel;
    if (telUrl.indexOf('tel:') === -1) {
      telUrl = 'tel:' + tel;
    }

    window.open(telUrl);
    this.onWindowOpen.emit(telUrl);
    this.onChatItemActionCardBehavior.emit({
      behavior: behavior,
      success: true,
      chatItem: this
    });
    this.chatLoggingService.info({
      logMessage: 'chat-item.component.launchDialer() success'
    });
  }

  onActionCardEvent($event: string) {
    this.onChatItemActionCardEvent.emit($event);
  }

  onActionCardBehavior($event: ActionCardBehaviorEvent) {
    $event.chatItem = this;
    this.onChatItemActionCardBehavior.emit($event);
  }

  onOpenLink($event: OpenLinkEvent) {
    $event.chatItem = this;
    this.onChatItemOpenLink.emit($event);
  }

  onPostWebhookPayload($event: PostWebhookPayloadEvent) {
    $event.chatItem = this;
    this.onChatItemPostWebhookPayload.emit($event);
  }

  onSendWebhookPayload($event: SendWebhookPayloadEvent) {
    $event.chatItem = this;
    this.onChatItemSendWebhookPayload.emit($event);
  }

  onSendActionCardMessage($event: ActionCardMessageEvent) {
    this.onChatItemActionCardMessage.emit($event);
  }

  public async loadImageAttachment(attachmentId: string | null = null): Promise<string> {
    //Already loaded
    if (this.attachmentImageLoadCheck || this.blobUrl() || this.imageViewerUrl()) {
      return;
    }
    var nearBottom = (this.loading() && !this.isSnapshot()) || this.scrollingService.isNearBottom();
    this.isAttachmentImage.set(true);
    this.cdRef.markForCheck();
    const id: string = attachmentId ? attachmentId : this.attachmentId() ? this.attachmentId() : this.item().body;
    try {
      this.attachmentImageLoadCheck = true;
      const response = await this.chatConversationService.getAttachmentAsBlob({
        conversationId: this.item().conversationId,
        attachmentId: id
      });

      loadImage.parseMetaData(
        <any>response,
        data => {
          var orientation = null;
          if (data.exif) {
            orientation = data.exif.get('Orientation');
          }
          this.chatLoggingService.debug({
            logMessage: 'chat-item.component.loadImageAttachment(), parseMetaData',
            logParameters: [this.item().messageId, !!data.exif, orientation]
          });
          if (orientation && orientation != 1) {
            loadImage(
              response,
              (img, data) => {
                img.toBlob(blob => {
                  if (this.chatCloudLinkService.browser.name === 'ios-webview') {
                    var reader = new FileReader();
                    reader.onload = e => {
                      this.loadImage(reader.result, nearBottom);
                    };
                    reader.readAsDataURL(blob);
                  } else {
                    this.blobUrl.set(window.URL.createObjectURL(blob));
                    this.loadImage(this.blobUrl(), nearBottom);
                  }
                });
              },
              { orientation: orientation }
            );
          } else {
            if (this.chatCloudLinkService.browser.name === 'ios-webview') {
              var reader = new FileReader();
              reader.onload = e => {
                this.loadImage(reader.result, nearBottom);
              };
              reader.readAsDataURL(response);
            } else {
              this.blobUrl.set(window.URL.createObjectURL(response));
              this.loadImage(this.blobUrl(), nearBottom);
            }
          }
        },
        { disableImageHead: true }
      );
    } catch (ex) {
      this.isAttachmentImage.set(false);
      this.cdRef.markForCheck();
      this.chatLoggingService.error({
        logMessage: 'chat-item.component.loadImageAttachment(): Error',
        logParameters: [ex]
      });
      throw ex;
    }
    return this.blobUrl();
  }

  private loadImage(url: any, nearBottomBefore: boolean = null) {
    var nearBottom = nearBottomBefore || (this.loading() && !this.isSnapshot()) || this.scrollingService.isNearBottom();
    var scrolledToMessage = this.isSnapshot() && !!this.scrollingService.lastScrolledTo.messageId;
    var img = new Image();
    img.src = url;
    img.onload = () => {
      let imageHeight = img.height;
      let imageHeighRatio = img.width / img.height;

      if (imageHeight > 360) {
        this.imageWidth.set(imageHeighRatio * 360);
      } else {
        this.imageWidth.set(img.width);
      }

      this.imageLoading.set(false);
      if (!this.cdRef['destroyed']) {
        this.cdRef.markForCheck();
      }
      setTimeout(() => {
        if (nearBottom && !this.goingToQuoted() && !scrolledToMessage) {
          this.scrollingService.scrollToBottom();
        } else if (scrolledToMessage && !this.goingToQuoted()) {
          this.scrollingService.scrollToMessage(this.scrollingService.lastScrolledTo.messageId);
        }
      }, 0);
    };
    img.onerror = e => {
      this.imageLoading.set(false);
      this.isAttachmentImage.set(false);
      this.isMediaMessage.set(false);
      this.useImageViewer.set(false);
      this.isImageMessageWithContent.set(true);
      if (!this.cdRef['destroyed']) {
        this.cdRef.markForCheck();
      }
    };
    let safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    this.imageViewerUrl.set(safeUrl);
    this.isMediaMessage.set(true);
    this.useImageViewer.set(true);
  }

  private async loadPdfAttachment() {
    if (this.pdfViewerUrl()) {
      return;
    }
    try {
      let response = await this.chatConversationService.getAttachmentAsBlob({
        conversationId: this.item().conversationId,
        // CL-13857
        attachmentId: this.item().clCompChat_displayBody
      });

      //Verify the blob is actually a PDF
      let isPdf = await this.isPdf(response);
      if (!isPdf) {
        throw this.item().body + ' is not a PDF';
      }

      //Need to force it to a pdf type to display properly in an iframe
      response = response.slice(0, response.size, 'application/pdf');
      this.mediaHeightRatio.set(0.77272727272);
      if (
        this.chatCloudLinkService.browser.name === 'ios-webview' ||
        this.chatCloudLinkService.browser.name === 'safari'
      ) {
        await new Promise<void>(resolve => {
          let reader = new FileReader();
          reader.onload = e => {
            this.pdfViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(reader.result as string));
            resolve();
          };
          reader.readAsDataURL(response);
        });
      } else {
        this.blobUrl.set(window.URL.createObjectURL(response));
        this.pdfViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl()));
      }
    } catch (ex) {
      throw ex;
    }
  }

  async isPdf(blob: Blob): Promise<boolean> {
    return new Promise(resolve => {
      let reader = new FileReader();
      reader.onload = async e => {
        let arr = new Uint8Array(e.target.result as ArrayBuffer).subarray(0, 4);
        let header = '';
        for (var i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }
        resolve(header.indexOf('25504446') > -1);
      };
      reader.readAsArrayBuffer(blob.slice(0, 4));
    });
  }

  private parseLocation(positionObj = null) {
    const gmaps = /.*google.*maps.*@/g;
    const bing = /.*bing.*maps.*cp=/g;

    if (positionObj && positionObj.coords) {
      this.latitude.set(positionObj.coords.latitude);
      this.longitude.set(positionObj.coords.longitude);
    } else if (this.item().contentType === ContentTypes.LOCATION) {
      let position = JSON.parse(this.item().body);
      if (position && position.coords) {
        this.latitude.set(position.coords.latitude);
        this.longitude.set(position.coords.longitude);
      }
    } else if (this.item().body.match(gmaps)) {
      const matcher = new RegExp('@(-?[0-9]+.[0-9]+),(-?[0-9]+.[0-9]+)');
      const match = this.item().body.match(matcher);
      if (match) {
        this.latitude.set(+match[1]);
        this.longitude.set(+match[2]);
        this.contentType.set(ContentTypes.LOCATION);
      }
    } else if (this.item().body.match(bing)) {
      const matcher = new RegExp('=(-?[0-9]+.[0-9]+)~(-?[0-9]+.[0-9]+)');
      const match = this.item().body.match(matcher);
      if (match) {
        this.latitude.set(+match[1]);
        this.longitude.set(+match[2]);
        this.contentType.set(ContentTypes.LOCATION);
      }
    }
  }

  getReactionNameKey(emoji: string): string {
    return this.reactionNameKeys[emoji] || '';
  }

  async setReaction(reaction) {
    if (!this.settingReaction()[reaction]) {
      this.settingReaction.update(obj => ({ ...obj, [reaction]: true }));
      if (this.myReactions()[reaction]) {
        try {
          await this.chatCloudLinkService.chat.deleteReaction({
            conversationId: this.item().conversationId,
            messageId: this.item().messageId,
            reactionId: this.myReactions()[reaction]
          });
          this.removeReaction(reaction, this.chatAuthService.userId);
          this.settingReaction.update(obj => {
            const newObj = { ...obj };
            delete newObj[reaction];
            return newObj;
          });
        } catch (ex) {
          if (ex.statusCode && ex.statusCode === 404) {
            this.removeReaction(reaction, this.chatAuthService.userId);
          }
          this.settingReaction.update(obj => {
            const newObj = { ...obj };
            delete newObj[reaction];
            return newObj;
          });
          this.chatLoggingService.messageError({
            exceptionLogEvent: {
              logMessage: 'chat-item.component.setReaction(): Error deleting',
              logParameters: [reaction, ex]
            },
            errorMessage: this.translateService.instant('cl_chat.failedToRemoveReaction')
          });
        }
      } else {
        try {
          const res = await this.chatCloudLinkService.chat.createReaction({
            conversationId: this.item().conversationId,
            messageId: this.item().messageId,
            body: {
              reaction: reaction
            }
          });

          let result = res.body;
          this.addReaction(reaction, result.createdBy, result.reactionId);
          this.settingReaction.update(obj => {
            const newObj = { ...obj };
            delete newObj[reaction];
            return newObj;
          });
        } catch (ex) {
          this.settingReaction.update(obj => {
            const newObj = { ...obj };
            delete newObj[reaction];
            return newObj;
          });
          this.chatLoggingService.messageError({
            exceptionLogEvent: {
              logMessage: 'chat-item.component.setReaction(): Error adding',
              logParameters: [reaction, ex]
            },
            errorMessage: this.translateService.instant('cl_chat.failedToAddReaction')
          });
        }
      }
    }
  }

  addReaction(reaction, userId, reactionId) {
    var nearBottom = this.scrollingService.isNearBottom();
    var scrolledToMessage = this.isSnapshot() && !!this.scrollingService.lastScrolledTo.messageId;
    if (!this.reactions()[this.item().messageId]) {
      this.reactions()[this.item().messageId] = {};
    }
    if (!this.reactions()[this.item().messageId][reaction]) {
      this.reactions()[this.item().messageId][reaction] = [];
    }
    //Make sure reaction doesn't already exist
    if (!this.reactions()[this.item().messageId][reaction].some(r => r.userId === userId)) {
      this.reactions()[this.item().messageId][reaction].push({
        userId: userId,
        reactionId: reactionId
      });
    }

    if (userId === this.chatAuthService.userId && !this.myReactions()[reaction]) {
      this.myReactions.update(obj => ({ ...obj, [reaction]: reactionId }));
    }
    // Force immediate detection since this is called after async operations in setReaction()
    this.cdRef.detectChanges();
    if (nearBottom && !scrolledToMessage) {
      this.scrollingService.scrollToBottom();
    } else if (scrolledToMessage && !this.goingToQuoted()) {
      this.scrollingService.scrollToMessage(this.scrollingService.lastScrolledTo.messageId);
    }
  }

  removeReaction(reaction, userId) {
    if (this.reactions()[this.item().messageId] && this.reactions()[this.item().messageId][reaction]) {
      this.reactions()[this.item().messageId][reaction] = this.reactions()[this.item().messageId][reaction].filter(
        r => r.userId !== userId
      );
      if (this.reactions()[this.item().messageId][reaction].length == 0) {
        delete this.reactions()[this.item().messageId][reaction];
      }
      if (Object.keys(this.reactions()[this.item().messageId]).length == 0) {
        delete this.reactions()[this.item().messageId];
      }
    }
    if (userId === this.chatAuthService.userId && this.myReactions()[reaction]) {
      this.myReactions.update(obj => {
        const newObj = { ...obj };
        delete newObj[reaction];
        return newObj;
      });
    }
    // Force immediate detection since this is called after async operations in setReaction()
    this.cdRef.detectChanges();
  }

  reactionCount() {
    var count = 0;
    for (var reaction in this.reactions()[this.item().messageId]) {
      count += this.reactions()[this.item().messageId][reaction].length;
    }
    return count;
  }

  quoteMessageClick(event) {
    event.preventDefault();
    this.closeOptions();
    var quotedMessage: QuoteMessageCite = {
      messageId: this.item().messageId,
      createdBy: this.item().createdBy,
      createdOn: this.item().createdOn,
      body: this.item().clCompChat_displayBody
    };
    quotedMessage.body = this.convertMessageToText();
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.reply_message,
      attributes: ChatAnalyticAttributes
    };
    this.chatLoggingService.analyticEvent(analyticObj);
    this.onQuotedMessageChange.emit(quotedMessage);
  }

  goToQuotedMessage() {
    this.onGoToMessage.emit({
      messageId: this.quoteMessage().cite.messageId,
      forceSearch: false,
      errorMessage: this.translateService.instant('cl_chat.couldNotFindOriginalMessageRepliedTo')
    });
  }

  goToMessage() {
    if (this.isSearch()) {
      this.onGoToMessage.emit({
        messageId: this.item().messageId,
        forceSearch: true
      });
    }
  }

  goToAiSuggestions() {
    this.closeOptions();
    this.onGoToAiSuggestionsMessage.emit(this.item().messageId);
  }

  convertMessageToText() {
    if (this.item().contentType === ContentTypes.QUOTED) {
      var q: QuoteMessage = JSON.parse(this.item().body);
      return q.content.body;
    } else if (this.item().contentType === ContentTypes.LOCATION) {
      var location = JSON.parse(this.item().clCompChat_displayBody);
      return this.translateService.instant('cl_chat.sharedALocation');
    } else if (this.item().contentType === ContentTypes.ACTIONCARD) {
      var actionCardText = '';
      var actionCard = JSON.parse(this.item().clCompChat_displayBody);
      if (actionCard.title) {
        actionCardText = actionCard.title;
      } else if (actionCard.subTitle) {
        actionCardText = actionCard.subTitle;
      } else if (actionCard.body) {
        actionCardText = actionCard.body;
      }
      return actionCardText;
    } else if (this.item().contentType === ContentTypes.TEXT) {
      return this.item().body;
    } else if (this.item().contentType === ContentTypes.VOICEMAIL) {
      var voicemail = JSON.parse(this.item().clCompChat_displayBody);
      return this.translateService.instant('cl_chat.voicemail');
    } else if (this.item().contentType === ContentTypes.MISSEDCALL) {
      var missedCall = JSON.parse(this.item().clCompChat_displayBody);
      let missedCallText = '';
      if (missedCall?.record?.fromName) {
        missedCallText = this.translateService.instant('cl_chat.missedCall') + ': ' + missedCall.record.fromName;
      } else {
        missedCallText = this.translateService.instant('cl_chat.missedCall');
      }
      return missedCallText;
    } else if (this.item().contentType === ContentTypes.CLOUDFILELINK) {
      return MessageUtils.getCloudFileLink(this.item())?.name ?? this.item().fallbackText;
    } else if (
      this.item().contentType === ContentTypes.ATTACHMENT ||
      this.item().contentType === ContentTypes.ATTACHMENTSYSTEMMESSAGE
    ) {
      return this.attachmentName();
    } else if (this.item().contentType === ContentTypes.HTML) {
      let body = this.item().clCompChat_displayBody;
      let mentionRegex = new RegExp('<span class="mi-mention-placeholder" hidden="true" itemprop="(.*?)"></span>', 'g');
      let mentions = this.item().clCompChat_displayBody.match(mentionRegex);
      if (mentions && mentions.length) {
        mentions.forEach(m => {
          let idRegex = new RegExp('itemprop="(.*?)"');
          let userId = m.match(idRegex)[1];
          body = body.replace(m, userId);
        });
      }
      let sanitizedContent = this.sanitizer.sanitize(SecurityContext.HTML, body);
      let tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedContent;
      return tempDiv.textContent || tempDiv.innerText || '';
    } else if (this.item().contentType === ContentTypes.MSADAPTIVECARD) {
      var adaptiveCard = JSON.parse(this.item().clCompChat_displayBody);
      var actionCardText = '';
      let textBlock = (adaptiveCard?.body ?? []).filter(
        element => Object.values(element).indexOf('TextBlock') > -1 && element.hasOwnProperty('text')
      );
      if (textBlock.length > 0 && textBlock[0]?.text) {
        actionCardText = textBlock[0].text;
      } else {
        actionCardText = this.translateService.instant('cl_chat.adaptiveCard');
      }
      return actionCardText;
    }
    return this.item().clCompChat_displayBody;
  }

  retrySend() {
    if (this.item().clCompChat_state === 'failed') {
      this.onRetrySend.emit({
        messageId: this.item().messageId,
        body: this.item().body,
        contentType: this.item().contentType,
        fallbackText: this.item().fallbackText
      });
    }
  }

  retryLoad() {
    this.chatLoggingService.info({
      logMessage: 'chat-item.component.retryLoad(): Retrying to load attachment message',
      logParameters: [this.item().conversationId, this.item().messageId]
    });
    this.isErrorMessage.set(false);
    this.attachmentImageLoadCheck = false;
    this.isAttachmentError.set(false);
    this.parseMessage();
  }

  deletePendingSend() {
    if (this.item().clCompChat_state === 'failed' || this.item().clCompChat_state === 'pending') {
      this.onDeletePendingSend.emit(this.item().messageId);
    }
  }

  fullScreenImage() {
    if (!this.previewMode && this.imageViewerUrl()) {
      this.onFullScreenImage.emit({
        source: this.imageViewerUrl(),
        name: this.attachmentName(),
        id:
          MessageUtils.isAttachment(this.item()) || MessageUtils.isAttachmentSystemMessage(this.item())
            ? this.item().clCompChat_displayBody
            : undefined,
        item: this.item()
      });
    }
  }

  async fullScreenPdf() {
    this.closeOptions();
    try {
      if (this.fullScreenPdfOverride) {
        this.fullScreenPdfOverride({
          conversationId: this.item().conversationId,
          attachmentId: this.pdfUrl() ? this.pdfUrl() : this.item().body,
          name: this.attachmentName()
        });
      } else {
        if (!this.pdfViewerUrl()) {
          await this.loadPdfAttachment();
        }
        if (this.pdfViewerUrl()) {
          this.onFullScreenPdf.emit({
            source: this.pdfViewerUrl(),
            name: this.attachmentName(),
            id:
              MessageUtils.isAttachment(this.item()) || MessageUtils.isAttachmentSystemMessage(this.item())
                ? this.item().clCompChat_displayBody
                : undefined,
            item: this.item()
          });
        }
      }
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat-item.component.fullScreenPdf(): Error',
          logParameters: [ex]
        },
        errorMessage: this.translateService.instant('cl_chat.failedToPreview')
      });
    }
  }

  avatarMouseEnter(event) {
    if (!this.previewMode) {
      this.onAvatarMouseEnter.emit({
        userId: this.item().createdBy,
        mouseEvent: event
      });
    }
  }

  avatarMouseLeave(event) {
    if (!this.previewMode) {
      this.onAvatarMouseLeave.emit({
        userId: this.item().createdBy,
        mouseEvent: event
      });
    }
  }

  avatarContextMenu(event) {
    if (!this.previewMode) {
      this.onAvatarContextMenu.emit({
        userId: this.item().createdBy,
        mouseEvent: event
      });
    }
  }

  messageContextMenu(event) {
    if (!this.previewMode) {
      this.onMessageContextMenu.emit({
        userId: this.item().createdBy,
        body: this.convertMessageToText(),
        mouseEvent: event
      });
    }
  }

  getDisplayName(userId, forAvatar = false) {
    if (this.smsMessage()) {
      return this.item().createdBy;
    }

    if (forAvatar && /^\+[1-9]\d{10,14}$/.test(this.item().createdBy)) {
      // E.164 formatted phone number
      return '+';
    }

    // Guards a real, pre-existing gap independent of aiMode: without this, an undefined userId
    // (once past the sms/E.164 cases above, which legitimately don't need one) falls through to
    // chatAdminService.getPrincipal(undefined) - a malformed admin lookup request that cascades
    // into a Dexie error trying to cache its "Unknown User" result.
    if (!userId) {
      return '';
    }

    if (this.chatAdminService.displayNames[userId]) {
      return this.chatAdminService.displayNames[userId];
    } else {
      this.loadDisplayName(userId);
      return '';
    }
  }

  async loadDisplayName(userId) {
    this.participantsLoadedSub.unsubscribe();
    this.participantsLoadedSub = new Subscription();
    this.participantsLoadedSub.add(
      this.chatAdminService.participantsLoaded$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async loaded => {
        this.participantsLoadedSub.unsubscribe();
        if (loaded) {
          await this.chatAdminService.getPrincipal(userId);
          this.userLoadedSub.unsubscribe();
          this.userLoadedSub = new Subscription();
          if (this.chatAdminService.usersLoaded$[userId]) {
            this.userLoadedSub.add(
              this.chatAdminService.usersLoaded$[userId].pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
                this.userLoadedSub.unsubscribe();
                this.cdRef.markForCheck();
                if (this.isSystemMessage()) {
                  this.chatLoggingService.debug({
                    logMessage: 'chat-item.component.getDisplayName() isSystemMessage',
                    logParameters: [userId]
                  });
                  this.parseMessage();
                }
              })
            );
          }
        }
      })
    );
  }

  onMouseEnter(event) {
    this.openOptions();
  }

  onFocus(event) {
    this.openOptions();
  }

  onMouseLeave(event) {
    if (!this.showReactionsArea() && !this.showMoreOptions()) {
      this.closeOptions();
    }
  }

  openOptions() {
    if (!this.previewMode) {
      this.showOptionsArea.set(true);
      // Force immediate check since mouseenter doesn't trigger CD automatically in zoneless
      this.cdRef.detectChanges();
    }
  }

  closeOptions($event?: any) {
    if ($event && this.isEventInElement($event, this.chatItem.nativeElement)) {
      return;
    }
    this.showOptionsArea.set(false);
    this.showReactionsArea.set(false);
    this.showMoreOptions.set(false);
    this.cdRef.markForCheck();
  }

  async updateReactions(reactions) {
    if (!this.previewMode && reactions.count) {
      this.reactions()[this.item().messageId] = {};
      for (const reaction of reactions._embedded.items) {
        if (!this.reactions()[this.item().messageId][reaction.reaction]) {
          this.reactions()[this.item().messageId][reaction.reaction] = [];
        }
        //Make sure reaction doesn't already exist
        if (!this.reactions()[this.item().messageId][reaction.reaction].some(r => r.userId === reaction.createdBy)) {
          this.reactions()[this.item().messageId][reaction.reaction].push({
            userId: reaction.createdBy,
            reactionId: reaction.reactionId
          });
          if (reaction.createdBy === this.chatAuthService.userId) {
            this.myReactions.update(obj => ({ ...obj, [reaction.reaction]: reaction.reactionId }));
          }
        } else {
          //Delete this, it's a duplicate
          this.chatLoggingService.info({
            logMessage: 'chat-item.component.updateReactions() Deleting duplicate reaction',
            logParameters: [reaction.reactionId]
          });
          try {
            await this.chatCloudLinkService.chat.deleteReaction({
              conversationId: this.item().conversationId,
              messageId: this.item().messageId,
              reactionId: reaction.reactionId
            });
          } catch (ex) {
            this.chatLoggingService.info({
              logMessage: 'chat-item.component.updateReactions() Error deleting duplicate reaction',
              logParameters: [reaction.reactionId, ex]
            });
          }
        }
      }
    }
  }

  flipTranslation() {
    this.closeOptions();
    this.isTranslated.set(!this.isTranslated());
    this.parseMessage();
  }

  public showMessageOptions(): boolean {
    if (this.aiMode()) {
      return false;
    }

    if (
      MessageUtils.isWhisperError(this.item()) ||
      MessageUtils.isWhisperMessage(this.item()) ||
      MessageUtils.isErrorMessage(this.item()) ||
      MessageUtils.isSystemMessage(this.item()) ||
      MessageUtils.isPendingMessage(this.item()) ||
      MessageUtils.isFailedMessage(this.item()) ||
      this.isErrorMessage()
    ) {
      return false;
    }

    //Logic for config
    if (this.config()) {
      if (this.config().hideMessageOptions) {
        return false;
      }
      if (
        !this.config().showGoToAiSuggestions &&
        this.config().hideMessageOptionsReaction &&
        this.config().hideMessageOptionsReply
      ) {
        if (this.showPdfMessageOptions()) {
          return true;
        }
        if (this.showDownloadMessageOptions()) {
          return true;
        }
        return false;
      }
    }

    return true;
  }

  public showPdfMessageOptions(): boolean {
    return (
      (this.isPdfAttachment() || this.pdfViewerUrl()) && (!this.config() || !this.config().hideMessageOptionsPreviewPdf)
    );
  }

  public showMoreMessageOptions(): boolean {
    return (
      !this.config()?.hideMessageOptionsInfo ||
      (this.isMine() &&
        this.chatAuthService.allowEditDelete &&
        ((this.isTextContentType() && this.config()?.showMessageOptionsEdit) ||
          this.config()?.showMessageOptionsDelete))
    );
  }

  public showDownloadMessageOptions(): boolean {
    return (
      ((this.isMobile &&
        (this.contentType() === this.contentTypes.ATTACHMENT ||
          this.contentType() === this.contentTypes.ATTACHMENTSYSTEMMESSAGE) &&
        this.getSimpleType(this.item().clCompChat_attachmentContentType) === 'audio') ||
        ((this.contentType() === this.contentTypes.ATTACHMENT ||
          this.contentType() === this.contentTypes.ATTACHMENTSYSTEMMESSAGE) &&
          this.getSimpleType(this.item().clCompChat_attachmentContentType) === 'image') ||
        ((this.contentType() === this.contentTypes.ATTACHMENT ||
          this.contentType() === this.contentTypes.ATTACHMENTSYSTEMMESSAGE) &&
          this.getSimpleType(this.item().clCompChat_attachmentContentType) === 'video')) &&
      (!this.config() || !this.config().hideMessageOptionsDownload)
    );
  }

  public isEdited(): boolean {
    //Bug on platform https://github.com/mitel-networks/platform-api-conversations/issues/1299
    return Math.abs(new Date(this.item().createdOn).getTime() - new Date(this.item().modifiedOn).getTime()) > 100;
  }

  public detectChanges() {
    this.cdRef.markForCheck();
  }

  public markForCheck() {
    this.cdRef.markForCheck();
  }

  public getDropDownHeight() {
    let numRow = 0;

    if (this.isTextContentType() && this.config()?.showMessageOptionsEdit) {
      numRow++;
    }

    if (this.config()?.showMessageOptionsDelete) {
      numRow++;
    }

    return numRow * 35;
  }

  public showMoreDropDown(event) {
    this.showReactionsArea.set(false);

    let srcElement = event.srcElement;
    if (event.srcElement.ownerSVGElement) {
      srcElement = event.srcElement.ownerSVGElement;
    }

    let chatListBottom = document.querySelector('#text-area')?.getBoundingClientRect().top;

    let eventLeft = srcElement.getBoundingClientRect().bottom;
    let bottomDif = chatListBottom - eventLeft;
    let tresholdHeight = this.getDropDownHeight() + 20;

    let right = 17;
    let top = 19;

    if (bottomDif < tresholdHeight) {
      top = top - (tresholdHeight - bottomDif);
      right = 40;
    }
    this.moreOptionsRight = right / 14 + 'rem';
    this.moreOptionsTop = top / 14 + 'rem';
    this.showMoreOptions.set(!this.showMoreOptions());
    setTimeout(() => {
      if (this.scrollingService.isNearBottom()) {
        this.scrollingService.scrollToBottom();
      }
    }, 0);

    // NOTE: detectChanges() is required here instead of markForCheck() because we need
    // synchronous DOM updates for dropdown positioning. markForCheck() only schedules
    // change detection for later, which doesn't work when immediate layout calculations are needed.
    this.cdRef.detectChanges();
  }

  public openDeleteMessageDialog() {
    this.onOpenModal.emit({
      modal: 'ChatConfirmationDialogComponent',
      chatItem: this
    });
  }

  async deleteMessage() {
    this.chatLoggingService.info({
      logMessage: 'chat-item.component.deleteMessage()',
      logParameters: [this.item().conversationId, this.item().messageId]
    });
    if (this.contentType() === ContentTypes.ATTACHMENT && this.attachmentId()) {
      await MessageUtils.deleteAttachment(
        this.item(),
        this.chatLoggingService,
        this.chatConversationService,
        this.translateService
      );
    } else if (this.contentType() === ContentTypes.ATTACHMENTSYSTEMMESSAGE && this.attachmentId()) {
      await MessageUtils.deleteAttachment(
        this.item(),
        this.chatLoggingService,
        this.chatConversationService,
        this.translateService
      );
      await MessageUtils.checkAndDeleteAttachmentMessage(
        this.attachmentId(),
        this.item().conversationId,
        this.chatCloudLinkService,
        this.chatAuthService,
        this.chatLoggingService,
        this.chatConversationService,
        this.translateService
      );
      //Can't delete the system message itself
      this.chatCloudLinkService.sendMessagesDeleted(this.item().messageId, true);
      return;
    }
    try {
      await defer(() =>
        this.chatConversationService.deleteMessage({
          conversationId: this.item().conversationId,
          messageId: this.item().messageId
        })
      )
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              'chat-item.component.deleteMessage(): delete message ' +
                this.item().conversationId +
                ' ' +
                this.item().messageId,
              this.chatLoggingService,
              3,
              2000,
              true
            )
          )
        )
        .toPromise();
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat-item.component.deleteMessage(): Error deleting message',
          logParameters: [ex]
        },
        errorMessage: this.translateService.instant('cl_chat.errorDeletingMessage')
      });
    }
  }

  private subscribeKeyDown = event => {
    if (this.emojisPopover?.overlayVisible) {
      this.detectChanges();
    }
    this.lastMessageInput.set(this.editTextArea?.nativeElement?.innerHTML);
    if (!this.isMobile && this.mentionsDropdown?.overlayVisible) {
      switch (event.code) {
        case KeyboardKeys.ENTER:
        case KeyboardKeys.NUMPADENTER:
          event.preventDefault();
          this.selectDropdown();
          this.textAreaProps.update(props => {
            props.mentionPending = false;
            return props;
          });
          this.closeTextInputDropdowns();
          this.cdRef.markForCheck();
          break;
        case KeyboardKeys.ARROWUP:
          event.preventDefault();
          this.setDropdownIndex(this.textAreaProps().selectedDropdownIndex - 1);
          break;
        case KeyboardKeys.ARROWDOWN:
          event.preventDefault();
          this.setDropdownIndex(this.textAreaProps().selectedDropdownIndex + 1);
          break;
        case KeyboardKeys.ESCAPE:
          event.preventDefault();
          this.textAreaProps.update(props => {
            props.mentionPending = false;
            return props;
          });
          this.closeTextInputDropdowns();
          this.cdRef.markForCheck();
          break;
        case KeyboardKeys.TAB:
          // Close without preventDefault so focus still moves on
          this.textAreaProps.update(props => {
            props.mentionPending = false;
            return props;
          });
          this.closeTextInputDropdowns();
          this.cdRef.markForCheck();
          break;
        default:
          break;
      }
    }
  };

  public editMessage(event) {
    event.preventDefault();
    if (!this.editModeOpen) {
      if (this.isQuotedContentType()) {
        const editQuotedObj = JSON.parse(this.item().clCompChat_displayBody);
        this.editQuotedObjString.set(JSON.stringify(editQuotedObj));
        this.editQuotedBody.set(editQuotedObj.content.body);
        this.textAreaProps.update(props => {
          props.message = this.editQuotedBody();
          return props;
        });
      } else {
        this.textAreaProps.update(props => {
          props.message = this.item().body;
          return props;
        });
      }
      this.showOptionsArea.set(false);
      this.showMoreOptions.set(false);
      this.openEditMode();
      this.cdRef.markForCheck();
      if (this.scrollingService.isNearBottom()) {
        this.scrollingService.scrollToBottom();
      }
      this.checkMentions(this.textAreaProps(), 'message', () => {
        return this.editTextArea;
      });
      this.checkEmojis(this.textAreaProps(), 'message');
      this.setCaretToEnd();
      this.getCaretPosition();
    } else if (this.currentEditMessageId()) {
      // Scroll to put parent edit mode in view
      this.scrollingService.scrollToMessage(this.currentEditMessageId());
      let currentEditChatItem = this.chatItems().find(c => c.item().messageId === this.currentEditMessageId());
      if (currentEditChatItem) {
        currentEditChatItem.setCaretToEnd(); // Focus on edit text area
      }
      this.showMoreOptions.set(false);
    }
  }

  public selectMessages() {
    this.onSelectMessagesMode.emit(true);
    this.showMoreOptions.set(false);
    this.cdRef.markForCheck();
  }

  messageSelected() {
    if (this.isMine() && this.selectMessagesMode && !this.isSystemMessage() && !this.isErrorMessage()) {
      this.isMessageSelected = !this.isMessageSelected;
      this.onMessageSelected.emit(this.isMessageSelected);
      this.cdRef.markForCheck();
    }
  }

  public messageInfo(event) {
    event.preventDefault();
    this.onOpenModal.emit({
      modal: 'MessageInfoComponent',
      chatItem: this
    });
  }

  private getReadList(): Array<IChatMessageInfo> {
    let readList: Array<IChatMessageInfo> = [];
    if (this.isDelivered()) {
      //exclude from list myself,author
      this.participants()
        ?.filter(
          participant =>
            participant.userId != this.chatAuthService.userId &&
            participant.userId != this.item().createdBy &&
            this.item().clCompChat_readList?.includes(participant.userId)
        )
        .forEach(participant => {
          let name = this.getDisplayName(participant.userId);
          let imgSrc = this.chatAdminService.avatars[participant.userId]?.hasOwnProperty('50')
            ? this.chatAdminService.avatars[participant.userId][50]
            : '';
          readList.push({
            userId: participant.userId,
            name: name,
            imgSrc: imgSrc,
            lastReadMessageDate: participant.lastReadMessageDate
          });
        });
    }
    readList.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 0
    );
    return readList;
  }

  private getDeliveredList(): Array<IChatMessageInfo> {
    let deliveredList: Array<IChatMessageInfo> = [];
    if (this.isDelivered()) {
      //exclude from list myself,author and if person already read the message
      this.participants()
        ?.filter(
          participant =>
            participant.userId != this.chatAuthService.userId &&
            participant.userId != this.item().createdBy &&
            !this.item().clCompChat_readList?.includes(participant.userId)
        )
        .forEach(participant => {
          let name = this.getDisplayName(participant.userId);
          let imgSrc = this.chatAdminService.avatars[participant.userId]?.hasOwnProperty('50')
            ? this.chatAdminService.avatars[participant.userId][50]
            : '';
          deliveredList.push({
            userId: participant.userId,
            name: name,
            imgSrc: imgSrc,
            lastReadMessageDate: participant.lastReadMessageDate
          });
        });
    }
    deliveredList.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 0
    );
    return deliveredList;
  }

  setCaretToEnd() {
    if (this.editTextArea) {
      var el = this.editTextArea.nativeElement;
      if (el) {
        el.focus();
        if (typeof window.getSelection != 'undefined' && typeof document.createRange != 'undefined') {
          var range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false);
          var sel = MentionHelpers.getSelection(this.editTextArea);
          MentionHelpers.addRangeToSelection(range, sel);
        } else if (typeof (<any>document.body).createTextRange != 'undefined') {
          var textRange = (<any>document.body).createTextRange();
          textRange.moveToElementText(el);
          textRange.collapse(false);
          textRange.select();
        }
      }
    }
  }

  public showEditMode() {
    return this.editMode() && this.editModeOpen;
  }

  cancelEditMessage(e) {
    this.closeEditMode();
    this.cdRef.markForCheck();
  }

  openEditMode() {
    window.addEventListener('keydown', this.subscribeKeyDown);
    this.editMode.set(true);
    this.editModeOpen = true;
    this.editModeOpenChange.emit(true);
    this.currentEditMessageIdChange.emit(this.item().messageId);
    setTimeout(() => {
      if (SharedHelpers.isMobile()) {
        this.inputFunction = this.textInput.bind(this);
        this.editTextArea.nativeElement.addEventListener('input', this.inputFunction);
      }
    }, 0);
  }

  closeEditMode() {
    window.removeEventListener('keydown', this.subscribeKeyDown);
    if (this.emojiDropdownVisible()) {
      this.emojiDropdownVisible.set(false);
      if (this.emojisPopover) {
        this.emojisPopover.hide();
      }
    }
    if (this.editTextArea) {
      this.editTextArea.nativeElement.removeEventListener('input', this.inputFunction);
    }
    this.editMode.set(false);
    this.editModeOpen = false;
    this.editModeOpenChange.emit(false);
    this.currentEditMessageIdChange.emit(null);
    this.parseMessage();
  }

  async saveEditedMessage(e) {
    const isKeyboardEvent = e instanceof KeyboardEvent;
    // Shift+Enter inserts a newline; let the browser handle it
    if (isKeyboardEvent && e.shiftKey) {
      return;
    }
    e.preventDefault();

    // Ignore IME composition confirmation, held-down Enter, mention selection and in-flight saves
    if (
      isKeyboardEvent &&
      (e.isComposing || (!this.isMobile && e.keyCode === 229) || e.repeat || this.mentionsDropdown?.overlayVisible)
    ) {
      return;
    }
    if (this.isUpdatingMessage()) {
      return;
    }

    const text = this.editTextArea?.nativeElement?.innerText.trim();

    if (!text) {
      this.openDeleteMessageDialog();
      this.cancelEditMessage(e);
      return;
    }

    this.isUpdatingMessage.set(true);
    this.previewEditMessageContent.set(
      this.editTextArea?.nativeElement.innerHTML || this.item().clCompChat_displayBody
    );
    try {
      let body = MentionHelpers.convertMentionsToText(
        this.editTextArea,
        this.chatLoggingService,
        'chat-item.component'
      );
      if (this.config()?.enableTwemoji) {
        body = MentionHelpers.convertEmojisToText(this.editTextArea, this.chatLoggingService, 'chat-item.component');
      }
      let originalBodyCopy: string;
      if (this.isQuotedContentType()) {
        originalBodyCopy = body;
        const editQuotedObj = JSON.parse(this.editQuotedObjString());
        editQuotedObj.content.body = body;
        this.editQuotedObjString.set(JSON.stringify(editQuotedObj));
        body = this.editQuotedObjString();
      }
      if (this.item().body === body) {
        this.chatLoggingService.info({
          logMessage: 'chat-item.component.saveEditedMessage() same body, cancelling',
          logParameters: [this.item().conversationId, this.item().messageId]
        });
        this.cancelEditMessage(e);
        this.finishUpdating();
        return;
      }
      if (this.isQuotedContentType()) {
        const editQuotedObj = JSON.parse(this.editQuotedObjString());
        //shorten original message if needed
        let textToTakeLength =
          this.maxQuotedInputCharacters +
          this.maxQuotedMessageCite -
          SharedHelpers.getQuotedBodyLength(originalBodyCopy);
        let bodyToTake = SharedHelpers.shortenQuoteMessageCiteBody(textToTakeLength, editQuotedObj.cite.body);
        if (bodyToTake) {
          editQuotedObj.cite.body = bodyToTake;
          this.editQuotedObjString.set(JSON.stringify(editQuotedObj));
          body = this.editQuotedObjString();
        }
      }
      body = MessageUtils.removeTrailingNewLine(body);
      this.chatLoggingService.info({
        logMessage: 'chat-item.component.saveEditedMessage()',
        logParameters: [this.item().conversationId, this.item().messageId]
      });
      this.closeEditMode();
      await defer(() =>
        this.chatConversationService.editMessage({
          conversationId: this.item().conversationId,
          messageId: this.item().messageId,
          body: {
            body: body
          }
        })
      )
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              'chat-item.component.saveEditedMessage(): edit message ' +
                this.item().conversationId +
                ' ' +
                this.item().messageId,
              this.chatLoggingService,
              3,
              2000,
              true
            )
          )
        )
        .toPromise();
      this.finishUpdating();
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'chat-item.component.saveEditedMessage(): Error saving message',
          logParameters: [ex]
        },
        errorMessage: this.translateService.instant('cl_chat.errorSavingEditedMessage')
      });
      this.finishUpdating();
    }
  }

  private finishUpdating(): void {
    this.isUpdatingMessage.set(false);
    this.parseMessage();
  }

  private indexDbEnabled(): boolean {
    return this.config() && this.config().enableIndexDb && 'indexedDB' in window;
  }

  public nameClicked(userId: string) {
    if (!this.smsMessage()) {
      this.onNameClicked.emit(userId);
    }
  }

  openEmojiDropDown(show: boolean, target?: HTMLElement) {
    //If there isn't enough space below the chat item, it can cause whitespace to appear in Chrome because of the use of "transform"
    //We need to override it to use the regular absolute positioning
    this.overrideEmojiDropdownPos.set(false);
    let chatListEl: HTMLElement = document.getElementById('chat-list');
    if (this.chatItem?.nativeElement && chatListEl) {
      let offsetTop = this.chatItem.nativeElement.offsetTop;
      let chatListHeight = chatListEl.clientHeight;
      let spaceFromBottom = chatListHeight - offsetTop - this.chatItem.nativeElement.clientHeight;
      if (spaceFromBottom < 400) {
        this.overrideEmojiDropdownPos.set(true);
      }
    }

    if (show && this.emojisPopover && target) {
      this.emojisPopover.show(null, target);
    } else if (!show && this.emojisPopover) {
      this.emojisPopover.hide();
    }

    this.emojiDropdownVisible.set(show);
    if (!show && this.emojiPicker) {
      this.emojiPicker.clearSearch();
    }
    // Note: enableTwemoji is now set via template property binding [enableTwemoji]="config?.enableTwemoji"
    this.cdRef.markForCheck();
  }
  public onEmojiClick(e) {
    if (
      this.overCharacterLength(
        MentionHelpers.convertMentionsToText(this.editTextArea, this.chatLoggingService, 'chat-item.component').length +
          e.native.length
      )
    ) {
      this.onShowWarning.emit(this.translateService.instant('cl_chat.maximumTextInputCharacterLimitReached'));
    } else {
      MentionHelpers.onEmojiClick(
        e,
        this.editTextArea,
        this.textAreaProps(),
        this.chatCloudLinkService,
        this.chatLoggingService,
        'chat-item.component',
        this.cdRef,
        this.emojiPipe,
        this.isMobile,
        this.config()?.enableTwemoji
      );
    }
  }

  public getCaretPosition(checkForMention = false) {
    if (this.editTextArea) {
      MentionHelpers.getCaretPosition(
        this.editTextArea,
        this.textAreaProps(),
        this.chatCloudLinkService,
        this.chatLoggingService,
        'chat-item.component',
        checkForMention
      );
    }
  }

  public focusEditTextArea() {
    if (this.editTextArea) {
      this.editTextArea.nativeElement.focus();
    }
  }

  // The copy button lives inside markdown.pipe.ts's [innerHTML] output, so it has no Angular
  // bindings of its own - delegate the click here and read the code straight out of the DOM
  // rather than tracking a parallel index of rendered code blocks.
  public onCodeBlockClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement)?.closest<HTMLButtonElement>('.ai-code-copy-btn');
    if (!button || button.classList.contains('copied')) {
      return;
    }
    const code = button.parentElement?.querySelector('code')?.textContent ?? '';
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        const label = button.querySelector('.ai-code-copy-label');
        const originalLabel = label?.textContent;
        button.classList.add('copied');
        if (label) {
          label.textContent = this.translateService.instant('cl_chat.codeCopied');
        }
        setTimeout(() => {
          button.classList.remove('copied');
          if (label && originalLabel) {
            label.textContent = originalLabel;
          }
        }, 1500);
      })
      .catch(ex => {
        this.chatLoggingService.error({
          logMessage: 'chat-item.component.onCodeBlockClick(): Error copying code to clipboard',
          logParameters: [ex]
        });
      });
  }

  public onPaste(e: ClipboardEvent): void {
    e.preventDefault();
    if (!this.config() || !this.config().disablePasteFile) {
      //Handle pasting a file
      if (e?.clipboardData?.items?.length) {
        var item = e.clipboardData.items[0];
        if (item.kind === 'file') {
          return;
        }
      }
    }

    var text;
    if (e.clipboardData) {
      text = e.clipboardData.getData(ContentTypes.TEXT);
    } else if ((<any>window).clipboardData) {
      text = (<any>window).clipboardData.getData('Text');
    }
    //TODO Need to figure a way to figure out the actual mention length if it's selected. Edge case, so won't deal with it for now.
    //https://github.com/mitel-networks/cloudlink-components-chat/issues/1047
    let selectionLength = window?.getSelection()?.toString()?.length || 0;
    if (
      this.overCharacterLength(
        text.length +
          MentionHelpers.convertMentionsToText(this.editTextArea, this.chatLoggingService, 'chat-item.component')
            .length -
          selectionLength
      )
    ) {
      this.chatLoggingService.info({
        logMessage: 'chat-item.component.onPaste(): Max Input Characters exceeded'
      });

      this.onShowWarning.emit(this.translateService.instant('cl_chat.pasteExceedsTheMaximumTextInputCharacterLimit'));

      if (this.isQuotedContentType()) {
        text = text.substring(
          0,
          this.maxQuotedInputCharacters -
            MentionHelpers.convertMentionsToText(this.editTextArea, this.chatLoggingService, 'chat-item.component')
              .length +
            selectionLength
        );
      } else {
        text = text.substring(
          0,
          this.textAreaProps().maxInputCharacters -
            MentionHelpers.convertMentionsToText(this.editTextArea, this.chatLoggingService, 'chat-item.component')
              .length +
            selectionLength
        );
      }
    }
    if (document.queryCommandSupported('insertHTML')) {
      document.execCommand('insertHTML', false, escape(text));
    } else {
      document.execCommand('paste', false, text);
    }

    this.cdRef.markForCheck();
  }

  private overCharacterLength(length): boolean {
    if (this.isQuotedContentType() && length > this.maxQuotedInputCharacters) {
      return true;
    } else if (length > this.textAreaProps().maxInputCharacters) {
      return true;
    }
    return false;
  }

  private checkForMentions(key) {
    MentionHelpers.checkForMentions(
      key,
      this.editTextArea,
      this.textAreaProps(),
      this.mentionsDropdown,
      this.chatCloudLinkService,
      this.chatDatabaseService,
      this.chatAdminService,
      this.chatAuthService,
      this.cdRef,
      this.participants(),
      this.translateService.instant('cl_chat.here'),
      undefined,
      this.mentionsAnchor,
      this.dropdownMaxHeight()
    );
  }

  public enterMention(mention: User) {
    if (this.checkIfAbleToAddMention(mention.userId)) {
      let loadedMentionObservables = MentionHelpers.enterMention(
        mention,
        this.chatLoggingService,
        'chat-item.component',
        this.mentionsDropdown,
        this.textAreaProps(),
        this.editTextArea,
        this.mentionRefs,
        this.resolver,
        this.injector,
        this.appRef,
        this.cdRef
      );
      if (loadedMentionObservables.length) {
        MentionHelpers.waitForMentionsLoaded(
          loadedMentionObservables,
          this.ngUnsubscribe,
          this.cdRef,
          this.editTextArea,
          this.textAreaProps(),
          this.chatLoggingService,
          this.chatCloudLinkService,
          'chat-item.component',
          () => this.setCaretToEnd(),
          null,
          false
        );
      }
    } else {
      this.onShowWarning.emit(this.translateService.instant('cl_chat.maximumTextInputCharacterLimitReached'));
    }
  }

  private checkIfAbleToAddMention(userId) {
    //Need to add 1 for the space that's added after the mention
    let textLength = MentionHelpers.convertMentionsToText(
      this.editTextArea,
      this.chatLoggingService,
      'chat-item.component'
    ).length;
    let textToRemoveLength = ('@' + this.textAreaProps()?.mentionInput).length || 0;
    return !this.overCharacterLength(textLength - textToRemoveLength + ('@' + userId).length + 1);
  }

  async onEditKeyPress(event) {
    //Need to add 1 for the character that's going to be inputed
    if (
      SharedHelpers.isIgnoreKey(event) &&
      this.overCharacterLength(
        MentionHelpers.convertMentionsToText(this.editTextArea, this.chatLoggingService, 'chat-item.component').length +
          event.key.length
      )
    ) {
      this.onShowWarning.emit(this.translateService.instant('cl_chat.maximumTextInputCharacterLimitReached'));
      event.preventDefault();
    }
  }

  async onKeyUp(event) {
    var key = event.key;

    if (event.keyCode === 229) {
      key = this.textAreaProps().lastInputValue;
    }
    this.getCaretPosition(
      this.lastMessageInput() === this.editTextArea.nativeElement.innerHTML &&
        (key === KeyboardKeys.BACKSPACE || event.code === KeyboardKeys.DELETE || event.code === KeyboardKeys.BACKSPACE)
    );
    if (MentionHelpers.isBackspaceMention(key, event.code, this.textAreaProps())) {
      MentionHelpers.deleteBackspaceMention(
        this.chatCloudLinkService,
        this.chatLoggingService,
        'chat-item.component',
        this.textAreaProps(),
        this.editTextArea
      );
    } else if (
      (key === KeyboardKeys.BACKSPACE || event.code === KeyboardKeys.DELETE) &&
      this.textAreaProps().lastNode &&
      this.textAreaProps().lastNode.className === 'text-area' &&
      this.textAreaProps().lastNode.innerHTML === '<br>'
    ) {
      this.textAreaProps.update(props => {
        props.lastNode.innerHTML = '';
        return props;
      });
      this.closeTextInputDropdowns();
      MentionHelpers.removeTooltip();
    } else {
      MentionHelpers.checkForEmojis(
        event,
        this.emojiDictionary(),
        this.textAreaProps(),
        this.editTextArea,
        this.chatCloudLinkService,
        this.chatLoggingService,
        'chat-item.component'
      );
      if (key) {
        if (!this.config() || !this.config().hideMentions) {
          this.checkForMentions(key);
        }
      }
    }
    this.cdRef.markForCheck();
  }

  selectDropdown() {
    let user = this.textAreaProps().mentions[this.textAreaProps().selectedDropdownIndex];
    if (MentionHelpers.isMentionsDropdownOpen(this.mentionsDropdown, this.textAreaProps())) {
      this.enterMention(user);
    }
  }

  public restoreTextAreaFocus() {
    MentionHelpers.restoreTextAreaFocus(this.editTextArea);
  }

  public closeTextInputDropdowns() {
    if (this.mentionsDropdown) {
      this.mentionsDropdown.hide();
    }
  }

  setDropdownIndex(index) {
    MentionHelpers.setDropdownIndex(
      index,
      this.textAreaProps(),
      this.mentionsDropdown,
      this.dropdownMentions,
      this.cdRef
    );
  }

  //Need this for mobile to determine the key that was pressed down
  public textInput(event) {
    MentionHelpers.textInputEvent(event, this.textAreaProps());
  }

  public getChatMessageMaxWidth(): string {
    if (this.imageWidth() && !this.isAttachmentImage() && this.imageWidth() < 600) {
      return this.imageWidth() < this.chatItem?.nativeElement.clientWidth ? this.imageWidth() / 14 + 'rem' : '90%';
    } else if (this.isMediaMessage()) {
      return '37.5rem';
    } else if (this.contentType() === this.contentTypes.LOCATION) {
      return '37.5rem';
    } else if (this.isWhisper()) {
      return 'calc(90% - 1.6875rem)';
    } else if (this.contentType() === this.contentTypes.ACTIONCARD) {
      return this.actionCardMaxWidth;
    } else if (this.selectMessagesMode) {
      return 'calc(90% - 1.6875rem)';
    }
    return '90%';
  }

  public getChatMessageAvatarReadMaxWidth(): string {
    if (this.imageWidth() && !this.isAttachmentImage() && this.imageWidth() < 600) {
      return this.imageWidth() / 14 + 'rem';
    } else if (this.isMediaMessage()) {
      return '37.5rem';
    } else if (this.contentType() === this.contentTypes.LOCATION) {
      return '37.5rem';
    } else if (this.isWhisper()) {
      return 'calc(100% - 1.6875rem)';
    } else if (this.contentType() === this.contentTypes.ACTIONCARD) {
      return this.actionCardMaxWidth;
    }
    return '100%';
  }

  public trackByFnRead(index, item: string) {
    return item;
  }

  public isReadByAll(): boolean {
    let readByAll: boolean;
    if (this.participants()) {
      readByAll = this.participants().every(participant => {
        return new Date(participant?.lastReadMessageDate) >= new Date(this.item().createdOn);
      });
    }
    return (
      readByAll &&
      !this.noReadIndicators() &&
      !this.isSearch() &&
      !this.isSnapshot() &&
      !this.previewMode &&
      !(
        this.item().clCompChat_state === 'sending' ||
        this.item().clCompChat_state === 'failed' ||
        this.item().clCompChat_state === 'pending' ||
        this.isErrorMessage() ||
        this.isAttachmentError()
      )
    );
  }

  public isDelivered(): boolean {
    return (
      !this.noReadIndicators() &&
      !this.isSearch() &&
      !this.isSnapshot() &&
      !(
        this.item().clCompChat_state === 'sending' ||
        this.item().clCompChat_state === 'failed' ||
        this.item().clCompChat_state === 'pending' ||
        this.isErrorMessage() ||
        this.isAttachmentError()
      ) &&
      !this.previewMode
    );
  }

  private isEventInElement($event, element) {
    let rect = element.getBoundingClientRect();
    let x = $event.clientX;
    if (x < rect.left || x >= rect.right) return false;
    let y = $event.clientY;
    if (y < rect.top || y >= rect.bottom) return false;
    return true;
  }

  public imageSource(item) {
    if (SharedHelpers.validateE164Num(item.createdBy)) {
      if (this.config()?.isOneToOneChat) {
        return SharedHelpers.resolveAssetUrl(this.assetBaseUrl, 'assets/images/cl-chat/one-one-sms.svg');
      } else {
        return SharedHelpers.resolveAssetUrl(this.assetBaseUrl, 'assets/images/cl-chat/textGroup-external.svg');
      }
    } else if (this.chatAdminService.avatars[item.createdBy]?.hasOwnProperty('150')) {
      return this.chatAdminService.avatars[item.createdBy][150];
    } else {
      return '';
    }
  }
}
